import { useCallback, useEffect, useRef, useState } from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Alert,
  Badge,
  Button,
  Col,
  Container,
  Dropdown,
  Form,
  Modal,
  Row,
  Spinner
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import ActivationGuide from "../Common/ActivationGuide";
import api, { mediaUrl } from "../../api/axios";
import { useFeedbackMessage } from "../Common/Feedback/FeedbackHub";
import { adjustCardPhoto } from "../../utils/imageProcessing";
import LiveCardScanner from "./LiveCardScanner";
import { RarityMark } from "./RarityMark";
import BinderCardViewer from "./BinderCardViewer";
import BinderSwitcher from "./BinderSwitcher";
import { BinderSkeleton } from "../Common/Loading/PageSkeletons";

const DEFAULT_RARITIES = [
  { code: "common", display_name: "Comun", symbol_shape: "circle", symbol_count: 1, symbol_color: "black" },
  { code: "uncommon", display_name: "Infrecuente", symbol_shape: "diamond", symbol_count: 1, symbol_color: "black" },
  { code: "rare", display_name: "Rara", symbol_shape: "star", symbol_count: 1, symbol_color: "black" },
  { code: "double_rare", display_name: "Rara Doble", symbol_shape: "star", symbol_count: 2, symbol_color: "black" },
  { code: "ultra_rare", display_name: "Rara Ultra", symbol_shape: "star", symbol_count: 2, symbol_color: "silver" },
  { code: "illustration_rare", display_name: "Rara Ilustracion", symbol_shape: "star", symbol_count: 1, symbol_color: "gold" },
  { code: "special_illustration_rare", display_name: "Rara Ilustracion Especial", symbol_shape: "star", symbol_count: 2, symbol_color: "gold" },
  { code: "hyper_rare", display_name: "Rara Hiper", symbol_shape: "star", symbol_count: 3, symbol_color: "gold" },
  { code: "pikachu_rare", display_name: "Rara Pikachu", symbol_shape: "star", symbol_count: 1, symbol_color: "gold" },
  { code: "mega_attack_rare", display_name: "Rara Ataque Mega", symbol_shape: "star", symbol_count: 1, symbol_color: "gold" },
  { code: "mega_hyper_rare", display_name: "Rara Hiper Mega", symbol_shape: "star", symbol_count: 3, symbol_color: "gold" },
  { code: "black_white_rare", display_name: "Rara Blanco y Negro", symbol_shape: "star", symbol_count: 1, symbol_color: "black" },
  { code: "futuristic_rare", display_name: "Rara Futurista", symbol_shape: "star", symbol_count: 1, symbol_color: "silver" },
  { code: "classic_collection", display_name: "Coleccion Clasica", symbol_shape: "star", symbol_count: 1, symbol_color: "gold" }
];

const rarityGlyph = rarity => {
  const glyph = rarity.symbol_shape === "circle" ? "\u25cf" : rarity.symbol_shape === "diamond" ? "\u25c6" : "\u2605";
  return glyph.repeat(Math.max(1, Number(rarity.symbol_count) || 1));
};

const CARD_FINISH_OPTIONS = [
  { value: "unspecified", label: "Sin especificar" },
  { value: "normal", label: "Normal" },
  { value: "holo", label: "Holo" },
  { value: "reverse_holo", label: "Reverse holo" },
  { value: "cosmos_holo", label: "Cosmos holo" },
  { value: "stamped", label: "Stamped" },
  { value: "other", label: "Otro" }
];
const cardFinishLabel = value => CARD_FINISH_OPTIONS.find(option => option.value === value)?.label || "Sin especificar";

const ENERGY_TYPE_OPTIONS = ["Incoloro", "Planta", "Fuego", "Agua", "Rayo", "Psiquico", "Lucha", "Oscuridad", "Metalico", "Dragon", "Hada", "No aplica"];
const normalizeEnergyType = value => {
  const key = String(value || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const aliases = {
    colorless: "Incoloro",
    incoloro: "Incoloro",
    incolora: "Incoloro",
    normal: "Incoloro",
    grass: "Planta",
    planta: "Planta",
    fire: "Fuego",
    fuego: "Fuego",
    water: "Agua",
    agua: "Agua",
    lightning: "Rayo",
    electric: "Rayo",
    rayo: "Rayo",
    psychic: "Psiquico",
    psiquico: "Psiquico",
    fighting: "Lucha",
    lucha: "Lucha",
    darkness: "Oscuridad",
    dark: "Oscuridad",
    oscuridad: "Oscuridad",
    oscuro: "Oscuridad",
    oscura: "Oscuridad",
    siniestro: "Oscuridad",
    metal: "Metalico",
    steel: "Metalico",
    metalico: "Metalico",
    metalica: "Metalico",
    dragon: "Dragon",
    fairy: "Hada",
    hada: "Hada",
    "not applicable": "No aplica",
    "no aplica": "No aplica"
  };
  return aliases[key] || ENERGY_TYPE_OPTIONS.find(option => option.toLowerCase() === key) || String(value || "").trim();
};

const intentionLabels = { collection: "En coleccion", open_to_trade: "Acepta propuestas", for_trade: "Para intercambio" };

const binderThemeLabels = {
  character: "Personaje",
  artist: "Artista",
  rarity: "Rareza",
  card_type: "Tipo de carta",
  energy_type: "Tipo elemental",
  custom: "Otro tema"
};
const binderThemeText = collection => collection?.theme_query
  ? `${binderThemeLabels[collection.theme_type] || "Tema"}: ${collection.theme_query}`
  : "";

const statusLabels = {
  all: "Todas",
  owned: "Tengo",
  missing: "Me faltan",
  wanted: "Busco",
  for_trade: "Intercambio"
};

const BINDER_CARDS_PER_PAGE = 9;
const MAX_BINDER_PAGES = 30;
const MAX_BINDER_CARDS = BINDER_CARDS_PER_PAGE * MAX_BINDER_PAGES;

const emptyCollectionForm = () => ({
  name: "",
  collectionType: "custom",
  binderIntent: "collection",
  binderStructure: "custom",
  defaultCardIntention: "collection",
  cardSetId: "",
  catalogLanguage: "es",
  visibility: "public",
  targetTotal: "",
  description: "",
  themeType: "",
  themeQuery: ""
});
const binderItemKey = card => card?.id ? `user-card:${card.id}` : `catalog-card:${card?.catalog_card_id || card?.card_id}`;
const binderDragId = card => `drag:${binderItemKey(card)}`;
const userCardIdFromDragId = dragId => String(dragId || "").replace(/^drag:user-card:/, "");
const newestPhotoFirst = photos => [...(photos || [])].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

const preferredPhoto = (card, types) => {
  const photos = newestPhotoFirst(card?.photos);
  return photos.find(photo => types.includes(photo.photo_type)) || photos[0] || null;
};

const cardFrontImage = card => {
  const frontPhoto = preferredPhoto(card, ["front", "binder"]);
  return frontPhoto?.photo_url || card?.image_url;
};

const catalogImageUrl = value => {
  const url = mediaUrl(value);
  return url && !/\.(webp|png|jpe?g|svg)(?:[?#]|$)/i.test(url) ? `${url}.webp` : url;
};
const baseSetTarget = set => set?.official_cards || set?.total_cards || "";

function CatalogSetImage({ set }) {
  const logoUrl = catalogImageUrl(set.logo_url);
  const symbolUrl = catalogImageUrl(set.symbol_url);
  const [src, setSrc] = useState(logoUrl || symbolUrl);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setSrc(logoUrl || symbolUrl);
    setLoaded(false);
  }, [logoUrl, symbolUrl]);

  if (!src) return <i className="bi bi-layers" />;
  return (
    <>
      {!loaded && <span className="binder-set-logo-skeleton" aria-hidden="true" />}
      <img
        src={src}
        alt=""
        className={loaded ? "is-loaded" : "is-loading"}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => {
          setLoaded(false);
          setSrc(current => current === logoUrl && symbolUrl && symbolUrl !== logoUrl ? symbolUrl : "");
        }}
      />
    </>
  );
}


function BinderCardImage({ src, alt, className = "" }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <span className={`binder-card-image ${loaded ? "is-loaded" : "is-loading"} ${className}`}>
      {!loaded && <span className="binder-card-image-skeleton" aria-hidden="true" />}
      <img src={src} alt={alt} loading="lazy" onLoad={() => setLoaded(true)} onError={() => setLoaded(true)} />
    </span>
  );
}

const BinderPocketIndicators = ({ card }) => (
  <span className="binder-pocket-indicators" aria-hidden="true">
    {card.is_missing && <i className="bi bi-x-lg is-missing" title="Falta" />}
    {card.status === "wanted" && <i className="bi bi-search-heart is-wanted" title="Buscada" />}
    {card.card_intention === "for_trade" && Number(card.available_quantity) > 0 && <i className="bi bi-arrow-left-right is-trade" title="Disponible" />}
    {card.id && preferredPhoto(card, ["front", "binder"]) && <i className="bi bi-camera-fill is-photo" title="Foto real" />}
    {card.id && !preferredPhoto(card, ["front", "binder"]) && <i className="bi bi-images is-reference" title="Catalogo" />}
  </span>
);
function SortableBinderPocket({ card, index, isActive, isDisabled, onSelect, rarityOptions }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: binderDragId(card),
    disabled: isDisabled
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <button
      ref={setNodeRef}
      className={`binder-pocket ${card.is_missing ? "is-missing" : ""} ${isActive ? "is-active" : ""} ${isDragging ? "is-dragging" : ""}`}
      style={style}
      type="button"
      onClick={onSelect}
      {...attributes}
      {...listeners}
    >
      <BinderCardImage src={mediaUrl(cardFrontImage(card))} alt={card.name} />
      <small>{index + 1}</small>
      <BinderPocketIndicators card={card} />
      {!preferredPhoto(card, ["front", "binder"]) && !card.is_missing && <span className="binder-pocket-source">Catalogo</span>}
      {Number(card.quantity) > 1 && <span className="binder-pocket-quantity">x{card.quantity}</span>}
      <RarityMark rarity={card.rarity} options={rarityOptions} compact />
      <span className={`binder-pocket-status is-${card.status || "showcase"}`}>
        {intentionLabels[card.card_intention] || statusLabels[card.status] || card.status || "Carta"}
      </span>
    </button>
  );
}


function BinderQuickActions({ isWanted = false, loadingAction = "", onHave, onPhoto, onWant, autoFocusAction = "", className = "" }) {
  const busy = Boolean(loadingAction);
  return (
    <div className={`binder-card-quick-actions ${className}`} role="group" aria-label="Acciones de la carta">
      <span className="binder-card-quick-action is-missing" title="Todavia no esta en tu coleccion">
        <span className="binder-card-action-circle"><i className="bi bi-x-lg" /></span>
        <small>Me falta</small>
      </span>
      {onHave && (
        <button type="button" className="binder-card-quick-action is-have" disabled={busy} onClick={onHave} autoFocus={autoFocusAction === "have"} title="Agregar a mi binder sin foto">
          <span className="binder-card-action-circle">{loadingAction === "have" ? <Spinner size="sm" /> : <i className="bi bi-check-lg" />}</span>
          <small>{loadingAction === "have" ? "Guardando" : "La tengo"}</small>
        </button>
      )}
      {(onWant || isWanted) && (
        <button type="button" className={`binder-card-quick-action is-wanted ${isWanted ? "is-active" : ""}`} disabled={busy || isWanted} onClick={onWant} title={isWanted ? "Ya esta en tus buscadas" : "Añadir a mis buscadas y activar coincidencias"}>
          <span className="binder-card-action-circle">{loadingAction === "wanted" ? <Spinner size="sm" /> : <i className="bi bi-search-heart" />}</span>
          <small>{loadingAction === "wanted" ? "Guardando" : isWanted ? "Buscada" : "Añadir a buscadas"}</small>
        </button>
      )}
      {onPhoto && (
        <button type="button" className="binder-card-quick-action is-photo" disabled={busy} onClick={onPhoto} title="Subir fotos de mi carta">
          <span className="binder-card-action-circle">{loadingAction === "photo" ? <Spinner size="sm" /> : <i className="bi bi-camera-fill" />}</span>
          <small>{loadingAction === "photo" ? "Abriendo" : "Con foto"}</small>
        </button>
      )}
    </div>
  );
}
function BinderHome() {
  const [activationParams, setActivationParams] = useSearchParams();
  const [activationProgress, setActivationProgress] = useState(null);
  const [cards, setCards] = useState([]);
  const [binderLoading, setBinderLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [filter, setFilter] = useState(() => localStorage.getItem("cardora:binderFilter") || "all");
  const [binderViewMode, setBinderViewMode] = useState(() => localStorage.getItem("cardora:binderViewMode") || "binder");
  const [showBinderOnboarding, setShowBinderOnboarding] = useState(() => localStorage.getItem("cardora:binderOnboardingDismissed") !== "true");
  const [showAddModal, setShowAddModal] = useState(false);
  const [scannerEnabled, setScannerEnabled] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [collectionStep, setCollectionStep] = useState(1);
  const [editingCollectionId, setEditingCollectionId] = useState(null);
  const [collections, setCollections] = useState([]);
  const [collectionsLoaded, setCollectionsLoaded] = useState(false);
  const [showBinderGuide, setShowBinderGuide] = useState(false);
  const [binderGuideStep, setBinderGuideStep] = useState(1);
  const [binderGuideChoice, setBinderGuideChoice] = useState("set");
  const [binderGuideSetSearch, setBinderGuideSetSearch] = useState("");
  const [binderGuideLanguage, setBinderGuideLanguage] = useState("es");
  const [binderGuideSetId, setBinderGuideSetId] = useState("");
  const [binderGuideOwnedCardId, setBinderGuideOwnedCardId] = useState("");
  const [binderGuidePreviewCard, setBinderGuidePreviewCard] = useState(null);
  const [binderGuideCards, setBinderGuideCards] = useState([]);
  const [isLoadingBinderGuideCards, setIsLoadingBinderGuideCards] = useState(false);
  const binderGuideTrackedEventsRef = useRef(new Set());
  const [activeCollectionId, setActiveCollectionId] = useState("all");
  const [binderPage, setBinderPage] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("showcase");
  const [selectedCardIntention, setSelectedCardIntention] = useState("collection");
  const [selectedCondition, setSelectedCondition] = useState("Near Mint");
  const [selectedCollectionId, setSelectedCollectionId] = useState("");
  const [addCardMode, setAddCardMode] = useState("custom");
  const [customCardForm, setCustomCardForm] = useState({
    name: "",
    setName: "",
    collectorNumber: "",
    rarity: "",
    cardType: "",
    energyType: "",
    cardFinish: "unspecified",
    language: "es"
  });
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedBackPhoto, setSelectedBackPhoto] = useState(null);
  const [selectedPhotoOriginal, setSelectedPhotoOriginal] = useState(null);
  const [selectedBackPhotoOriginal, setSelectedBackPhotoOriginal] = useState(null);
  const [photoAdjustments, setPhotoAdjustments] = useState({
    front: { rotation: 0, zoom: 1, offsetX: 0, offsetY: 0 },
    back: { rotation: 0, zoom: 1, offsetX: 0, offsetY: 0 }
  });
  const [selectedPhotoPreview, setSelectedPhotoPreview] = useState("");
  const [selectedBackPhotoPreview, setSelectedBackPhotoPreview] = useState("");
  const [photoOptimizationInfo, setPhotoOptimizationInfo] = useState({ front: "", back: "" });
  const [adjustingPhotoSide, setAdjustingPhotoSide] = useState(null);
  const [liveScannerSide, setLiveScannerSide] = useState(null);
  const photoAdjustmentRequestRef = useRef({ front: 0, back: 0 });
  const [selectedVisibility, setSelectedVisibility] = useState("public");
  const [selectedTradeNotes, setSelectedTradeNotes] = useState("");
  const [selectedEstimatedValue, setSelectedEstimatedValue] = useState("");
  const [scanFile, setScanFile] = useState(null);
  const [scanPreview, setScanPreview] = useState("");
  const [scanText, setScanText] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [catalogOptions, setCatalogOptions] = useState({ sets: [], rarities: DEFAULT_RARITIES });
  const [catalogSetSearch, setCatalogSetSearch] = useState("");
  const [customRarityMode, setCustomRarityMode] = useState(false);
  const [scanAttemptCount, setScanAttemptCount] = useState(0);
  const [scanStep, setScanStep] = useState("photo");
  const [scanNotice, setScanNotice] = useState("");
  const [wantedText, setWantedText] = useState("");
  const [wantedRealMatches, setWantedRealMatches] = useState([]);
  const [wantedCatalogMatches, setWantedCatalogMatches] = useState([]);
  const [isSearchingWanted, setIsSearchingWanted] = useState(false);
  const [isIdentifyingWanted, setIsIdentifyingWanted] = useState(false);
  const [selectedScanCardId, setSelectedScanCardId] = useState("");
  const [targetCatalogCardId, setTargetCatalogCardId] = useState("");
  const [targetSetCollectionId, setTargetSetCollectionId] = useState("");
  const [isAddingSetCard, setIsAddingSetCard] = useState(false);
  const [isScanningCard, setIsScanningCard] = useState(false);
  const [collectionForm, setCollectionForm] = useState(emptyCollectionForm);
  const [isSavingCollection, setIsSavingCollection] = useState(false);
  const [characterThemeOptions, setCharacterThemeOptions] = useState([]);
  const [isLoadingCharacterThemes, setIsLoadingCharacterThemes] = useState(false);
  const [isSavingCard, setIsSavingCard] = useState(false);
  const [binderActionLoading, setBinderActionLoading] = useState("");
  const [batchQueue, setBatchQueue] = useState([]);
  const [savedCardName, setSavedCardName] = useState("");
  const [catalogIssueType, setCatalogIssueType] = useState("incorrect_name");
  const [catalogIssueDetails, setCatalogIssueDetails] = useState("");
  const [catalogReportSubmitted, setCatalogReportSubmitted] = useState(false);
  const [isSubmittingCatalogReport, setIsSubmittingCatalogReport] = useState(false);
  const [catalogReportError, setCatalogReportError] = useState("");
  const [isProcessingPhoto, setIsProcessingPhoto] = useState(false);
  const [formError, setFormError] = useState("");
  const [orderNotice, setOrderNotice] = useState("");
  const [pendingDeleteCollectionId, setPendingDeleteCollectionId] = useState(null);
  const [collectionDeleteForm, setCollectionDeleteForm] = useState({ cardAction: "keep", targetCollectionId: "" });
  const [pendingDeleteCardId, setPendingDeleteCardId] = useState(null);
  const [showMobileCardModal, setShowMobileCardModal] = useState(false);
  const [showMissingCardsModal, setShowMissingCardsModal] = useState(false);
  const [showThemeSuggestionsModal, setShowThemeSuggestionsModal] = useState(false);
  const [themeSuggestions, setThemeSuggestions] = useState([]);
  const [ownedThemeSuggestions, setOwnedThemeSuggestions] = useState([]);
  const [themeSuggestionSource, setThemeSuggestionSource] = useState("mine");
  const [themeSuggestionSetFilter, setThemeSuggestionSetFilter] = useState("");
  const [selectedThemeSuggestionIds, setSelectedThemeSuggestionIds] = useState([]);
  const [isSavingThemeSuggestionsBulk, setIsSavingThemeSuggestionsBulk] = useState(false);
  const [isLoadingThemeSuggestions, setIsLoadingThemeSuggestions] = useState(false);
  const [themeSuggestionActionId, setThemeSuggestionActionId] = useState("");
  const [showBulkDuplicatesModal, setShowBulkDuplicatesModal] = useState(false);
  const [selectedDuplicateCardIds, setSelectedDuplicateCardIds] = useState([]);
  const [bulkDuplicateQuantities, setBulkDuplicateQuantities] = useState({});
  const [isSavingBulkDuplicates, setIsSavingBulkDuplicates] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showQuickCatalogSearch, setShowQuickCatalogSearch] = useState(false);
  const [quickCatalogSearch, setQuickCatalogSearch] = useState("");
  const [quickCatalogResults, setQuickCatalogResults] = useState([]);
  const [isSearchingQuickCatalog, setIsSearchingQuickCatalog] = useState(false);
  const [quickCatalogCard, setQuickCatalogCard] = useState(null);
  const [quickQuantity, setQuickQuantity] = useState(1);
  const [quickAvailableQuantity, setQuickAvailableQuantity] = useState(0);
  const [quickTradeEnabled, setQuickTradeEnabled] = useState(false);
  const [quickCondition, setQuickCondition] = useState("Near Mint");
  const [quickCardFinish, setQuickCardFinish] = useState("unspecified");
  const [isSavingQuickCard, setIsSavingQuickCard] = useState(false);
  const [selectedMissingCardIds, setSelectedMissingCardIds] = useState([]);
  const [isSavingWantedBulk, setIsSavingWantedBulk] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [missingAssignmentCard, setMissingAssignmentCard] = useState(null);
  const [isAssigningExisting, setIsAssigningExisting] = useState(false);
  const [assignmentSearch, setAssignmentSearch] = useState("");
  const [pendingManualAssignmentId, setPendingManualAssignmentId] = useState(null);
  const [editForm, setEditForm] = useState({
    status: "showcase",
    cardIntention: "collection",
    condition: "Near Mint",
    collectionId: "",
    visibility: "public",
    tradeNotes: "",
    estimatedValue: "",
    salePrice: "",
    tradeMode: "cards_only",
    quantity: 1,
    availableQuantity: 0,
    cardFinish: "unspecified",
    photo: null,
    backPhoto: null
  });
  const [previewSide, setPreviewSide] = useState("front");
  const normalizedSetName = customCardForm.setName.trim().toLowerCase();
  const isUnknownSet = Boolean(normalizedSetName) && !catalogOptions.sets.some(set =>
    [set.name, set.code, ...(set.aliases || [])].filter(Boolean).some(value => String(value).trim().toLowerCase() === normalizedSetName)
  );
  const scanWouldUseAi = Boolean(scanFile && !scanText.trim());
  const scanLimitReached = scanWouldUseAi && scanAttemptCount >= 2;
  const capturePreview = isAddingSetCard ? selectedPhotoPreview : scanPreview;
  const [isPreviewZoomed, setIsPreviewZoomed] = useState(false);
  const [previewZoomOrigin, setPreviewZoomOrigin] = useState({ x: 50, y: 50 });
  const currentUser = JSON.parse(localStorage.getItem("user") || "null");

  const loadBinder = useCallback(() => {
    return api.get("/binder/me").then(({ data }) => setCards(data)).catch(() => setCards([])).finally(() => setBinderLoading(false));
  }, []);

  const loadCollections = useCallback(() => {
    return api.get("/collections/me").then(({ data }) => {
      setCollections(data);
      const rememberedCollection = localStorage.getItem("cardora:lastBinderId");
      const validRemembered = data.some(collection => collection.id === rememberedCollection);
      setSelectedCollectionId(current => current || (validRemembered ? rememberedCollection : data[0]?.id || ""));
    }).catch(() => setCollections([])).finally(() => setCollectionsLoaded(true));
  }, []);

  const centsFromInput = value => {
    if (value === "" || value === null || value === undefined) return null;
    return Math.round(Number(value) * 100);
  };

  const applyPhotoAdjustment = async (file, onReady, side, adjustments = photoAdjustments[side]) => {
    if (!file) {
      onReady(null);
      setPhotoOptimizationInfo(current => ({ ...current, [side]: "" }));
      return;
    }
    setIsProcessingPhoto(true);
    setFormError("");
    const requestId = photoAdjustmentRequestRef.current[side] + 1;
    photoAdjustmentRequestRef.current[side] = requestId;
    try {
      const adjusted = await adjustCardPhoto(file, adjustments);
      if (photoAdjustmentRequestRef.current[side] !== requestId) return;
      onReady(adjusted);
      setPhotoOptimizationInfo(current => ({
        ...current,
        [side]: `${(file.size / 1024 / 1024).toFixed(1)} MB a ${(adjusted.size / 1024 / 1024).toFixed(1)} MB · ajustada`
      }));
    } catch (error) {
      onReady(null);
      setFormError(error.message || "No pudimos ajustar esa imagen.");
    } finally {
      if (photoAdjustmentRequestRef.current[side] === requestId) setIsProcessingPhoto(false);
    }
  };

  const useLiveCardCapture = file => {
    const side = liveScannerSide;
    if (side === "front") {
      setScanFile(file);
      setSelectedPhotoOriginal(file);
      setSelectedPhoto(file);
      setScanResult(null);
      setScanAttemptCount(0);
      setSelectedScanCardId("");
    } else if (side === "back") {
      setSelectedBackPhotoOriginal(file);
      setSelectedBackPhoto(file);
    }
    setPhotoAdjustments(current => ({ ...current, [side]: { rotation: 0, zoom: 1, offsetX: 0, offsetY: 0 } }));
    setPhotoOptimizationInfo(current => ({ ...current, [side]: "Detectada y recortada automaticamente" }));
    setLiveScannerSide(side === "front" ? "back" : null);
  };

  const processPhoto = async (file, onReady, side) => {
    if (!file) {
      if (side === "front") setSelectedPhotoOriginal(null);
      if (side === "back") setSelectedBackPhotoOriginal(null);
      onReady(null);
      setPhotoOptimizationInfo(current => ({ ...current, [side]: "" }));
      return;
    }
    if (side === "front") setSelectedPhotoOriginal(file);
    if (side === "back") setSelectedBackPhotoOriginal(file);
    const baseAdjustment = { rotation: 0, zoom: 1, offsetX: 0, offsetY: 0 };
    setPhotoAdjustments(current => ({ ...current, [side]: baseAdjustment }));
    await applyPhotoAdjustment(file, onReady, side, baseAdjustment);
  };

  const updatePhotoAdjustment = async (side, patch) => {
    const sourceFile = side === "front" ? selectedPhotoOriginal : selectedBackPhotoOriginal;
    const onReady = file => {
      if (side === "front") {
        setSelectedPhoto(file);
        if (showAddModal && scannerEnabled && addCardMode === "scan") {
          setScanFile(file);
          setScanResult(null);
          setScanAttemptCount(0);
          setSelectedScanCardId("");
        }
      } else {
        setSelectedBackPhoto(file);
      }
      if (showEditModal) {
        setEditForm(current => ({
          ...current,
          [side === "front" ? "photo" : "backPhoto"]: file
        }));
      }
    };
    if (!sourceFile) return;
    const nextAdjustment = { ...photoAdjustments[side], ...patch };
    setPhotoAdjustments(current => ({ ...current, [side]: nextAdjustment }));
    await applyPhotoAdjustment(sourceFile, onReady, side, nextAdjustment);
  };

  useEffect(() => {
    loadBinder();
    loadCollections();
    api.get("/users/me/onboarding-progress").then(({ data }) => setActivationProgress(data)).catch(() => {});
    api.get("/cards/scan/status").then(({ data }) => setScannerEnabled(Boolean(data?.enabled))).catch(() => setScannerEnabled(false));
  }, [loadBinder, loadCollections]);

  useEffect(() => {
    if (!activationProgress?.enabled || !collectionsLoaded || collections.length || localStorage.getItem("cardora:binderGuideSeen") === "true") return;
    setCollectionForm({ ...emptyCollectionForm(), binderStructure: "set" });
    setCollectionStep(2);
    setShowCollectionModal(true);
  }, [activationProgress?.enabled, collectionsLoaded, collections.length]);

  useEffect(() => {
    if (!showBinderGuide) return;
    const events = binderGuideTrackedEventsRef.current;
    if (!events.has("opened")) {
      events.add("opened");
      void api.post("/analytics/events", { eventType: "binder_guide_opened", metadata: { source: "binder" } }).catch(() => undefined);
    }
    const stepKey = `step:${binderGuideStep}`;
    if (!events.has(stepKey)) {
      events.add(stepKey);
      void api.post("/analytics/events", { eventType: "binder_guide_step_viewed", metadata: { step: binderGuideStep } }).catch(() => undefined);
    }
  }, [showBinderGuide, binderGuideStep]);

  useEffect(() => {
    if (!showBinderGuide || binderGuideCards.length) return;
    setIsLoadingBinderGuideCards(true);
    api.get("/cards")
      .then(({ data }) => {
        const seenNames = new Set();
        const sample = (Array.isArray(data) ? data : [])
          .filter(card => card.image_url && !seenNames.has(String(card.name || "").toLowerCase()) && seenNames.add(String(card.name || "").toLowerCase()))
          .slice(0, 6);
        setBinderGuideCards(sample);
      })
      .catch(() => setBinderGuideCards([]))
      .finally(() => setIsLoadingBinderGuideCards(false));
  }, [showBinderGuide, binderGuideCards.length]);

  useEffect(() => {
    api.get("/cards/catalog-options", { params: { gameSlug: "pokemon" } })
      .then(({ data }) => setCatalogOptions({
        sets: data.sets || [],
        rarities: data.rarities?.length ? data.rarities : DEFAULT_RARITIES
      }))
      .catch(() => setCatalogOptions(current => ({ ...current, rarities: DEFAULT_RARITIES })));
  }, []);

  useEffect(() => {
    if (!showCollectionModal || collectionForm.binderStructure !== "custom" || collectionForm.themeType !== "character") {
      setCharacterThemeOptions([]);
      setIsLoadingCharacterThemes(false);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setIsLoadingCharacterThemes(true);
      try {
        const { data } = await api.get("/collections/theme-options/characters", {
          params: { q: collectionForm.themeQuery.trim(), limit: 24 },
          signal: controller.signal
        });
        setCharacterThemeOptions(Array.isArray(data) ? data : []);
      } catch (error) {
        if (error?.name !== "CanceledError" && error?.code !== "ERR_CANCELED") setCharacterThemeOptions([]);
      } finally {
        if (!controller.signal.aborted) setIsLoadingCharacterThemes(false);
      }
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [showCollectionModal, collectionForm.binderStructure, collectionForm.themeType, collectionForm.themeQuery]);

  useEffect(() => {
    if (selectedCollectionId) localStorage.setItem("cardora:lastBinderId", selectedCollectionId);
  }, [selectedCollectionId]);


  useEffect(() => {
    if (!orderNotice) return undefined;
    const orderNoticeTimer = setTimeout(() => setOrderNotice(""), 3200);
    return () => clearTimeout(orderNoticeTimer);
  }, [orderNotice]);

  useEffect(() => {
    if (!selectedPhoto) {
      setSelectedPhotoPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(selectedPhoto);
    setSelectedPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedPhoto]);

  useEffect(() => {
    if (!scanFile) {
      setScanPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(scanFile);
    setScanPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [scanFile]);

  useEffect(() => {
    if (!selectedBackPhoto) {
      setSelectedBackPhotoPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(selectedBackPhoto);
    setSelectedBackPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedBackPhoto]);

  const catalogLanguages = [...new Set(catalogOptions.sets.map(set => set.language).filter(Boolean))]
    .sort((left, right) => left === "es" ? -1 : right === "es" ? 1 : left.localeCompare(right));
  const catalogSetSearchTerm = catalogSetSearch.trim().toLowerCase();
  const filteredCatalogSets = catalogOptions.sets.filter(set => {
    if (set.language !== collectionForm.catalogLanguage) return false;
    if (!catalogSetSearchTerm) return true;
    return [set.name, set.code, ...(set.aliases || [])]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(catalogSetSearchTerm));
  });
  const binderGuideSetTerm = binderGuideSetSearch.trim().toLowerCase();
  const binderGuideSets = catalogOptions.sets
    .filter(set => set.language === binderGuideLanguage)
    .filter(set => !binderGuideSetTerm || [set.name, set.code, ...(set.aliases || [])].filter(Boolean).some(value => String(value).toLowerCase().includes(binderGuideSetTerm)))
    .slice(0, 6);
  const catalogLanguageLabel = value => ({ es: "Español", en: "Inglés", jp: "Japonés", ja: "Japonés" })[value] || String(value || "").toUpperCase();

  const activeCollection = collections.find(collection => collection.id === activeCollectionId);
  const rawSourceCards = activeCollectionId === "all"
    ? cards
    : cards.filter(card => card.collection_id === activeCollectionId);
  const isSetBinder = activeCollection?.binder_structure === "set";
  const isCustomBinder = activeCollection?.binder_structure === "custom";
  const isCharacterBinder = activeCollection?.binder_structure === "custom" && activeCollection?.theme_type === "character" && Boolean(activeCollection?.theme_query);
  const isCatalogBinder = isSetBinder || isCharacterBinder;
  const uniqueSourceCards = rawSourceCards.filter((card, index, list) =>
    list.findIndex(item => binderItemKey(item) === binderItemKey(card)) === index
  );
  const sourceCards = isCatalogBinder
    ? [...uniqueSourceCards].sort((left, right) => new Intl.Collator("es", { numeric: true }).compare(String(left.collector_number || ""), String(right.collector_number || "")))
    : uniqueSourceCards;
  const isOwnedBinderCard = card => Boolean(card.id) && !card.is_missing && card.status !== "wanted";
  const ownedCardsInBinder = sourceCards.filter(isOwnedBinderCard);
  const bulkDuplicateCards = ownedCardsInBinder.filter(card => Number(card.quantity || 1) < 99);
  const selectedDuplicateCopies = selectedDuplicateCardIds.reduce(
    (total, cardId) => total + (Number(bulkDuplicateQuantities[cardId]) || 1),
    0
  );
  const allBindersOwnedCount = cards.filter(isOwnedBinderCard).length;
  const filteredCards = sourceCards.filter(card => {
    if (filter === "all") return activeCollectionId === "all" ? isOwnedBinderCard(card) : true;
    if (filter === "owned") return Boolean(card.id) && !card.is_missing && card.status !== "wanted";
    if (filter === "missing") return Boolean(card.is_missing) && card.status !== "wanted";
    if (filter === "wanted") return card.status === "wanted";
    if (filter === "for_trade") return card.card_intention === "for_trade" && Number(card.available_quantity) > 0;
    return true;
  });
  const filterCounts = {
    all: activeCollectionId === "all" ? allBindersOwnedCount : sourceCards.length,
    owned: sourceCards.filter(isOwnedBinderCard).length,
    missing: sourceCards.filter(card => card.is_missing && card.status !== "wanted").length,
    wanted: sourceCards.filter(card => card.status === "wanted").length,
    for_trade: sourceCards.filter(card => card.card_intention === "for_trade" && Number(card.available_quantity) > 0).length
  };
  const binderPageCount = Math.max(1, Math.ceil(filteredCards.length / BINDER_CARDS_PER_PAGE));
  const currentBinderPageCards = filteredCards.slice(
    binderPage * BINDER_CARDS_PER_PAGE,
    binderPage * BINDER_CARDS_PER_PAGE + BINDER_CARDS_PER_PAGE
  );
  const currentDraggableIds = currentBinderPageCards
    .filter(card => card.id && !card.is_missing)
    .map(binderDragId);
  const emptyPocketCount = Math.max(0, BINDER_CARDS_PER_PAGE - currentBinderPageCards.length);
  const activeCard = filteredCards[activeIndex] || filteredCards[0] || sourceCards[0] || null;
  const activeCardHasFrontPhoto = activeCard?.photos?.some(photo => ["front", "binder"].includes(photo.photo_type));
  const activeCardIsVisuallyMissing = activeCard?.is_missing && !activeCardHasFrontPhoto;
  const normalizeCardIdentity = value => String(value || "").trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "");
  const isExactAssignmentCandidate = card => Boolean(missingAssignmentCard) && (
    (card.card_id && card.card_id === missingAssignmentCard.catalog_card_id)
    || (
      normalizeCardIdentity(card.name) === normalizeCardIdentity(missingAssignmentCard.name)
      && normalizeCardIdentity(card.collector_number) === normalizeCardIdentity(missingAssignmentCard.collector_number)
    )
  );
  const assignmentCandidates = missingAssignmentCard ? cards
    .filter(card => card.id && !card.collection_id && card.status !== "wanted" && !card.is_missing)
    .filter(card => {
      const search = normalizeCardIdentity(assignmentSearch);
      if (!search) return true;
      return [card.name, card.set_name, card.collector_number].some(value => normalizeCardIdentity(value).includes(search));
    })
    .sort((left, right) => Number(isExactAssignmentCandidate(right)) - Number(isExactAssignmentCandidate(left)))
    .slice(0, 60) : [];
  const missingSetCards = isSetBinder ? sourceCards.filter(card => card.is_missing) : [];
  const markableMissingCards = missingSetCards.filter(card => card.status !== "wanted" && card.catalog_card_id);
  const selectableMissingCards = missingSetCards.filter(card => card.catalog_card_id);
  const ownedCardsCount = sourceCards.filter(isOwnedBinderCard).length;
  const ownedCopiesCount = sourceCards
    .filter(isOwnedBinderCard)
    .reduce((total, card) => total + (Number(card.quantity) || 1), 0);
  const completionCardIds = new Set(sourceCards.map(card => card.catalog_card_id || card.card_id).filter(Boolean));
  const completedCardIds = new Set(sourceCards
    .filter(isOwnedBinderCard)
    .map(card => card.catalog_card_id || card.card_id)
    .filter(Boolean));
  const completion = isCatalogBinder && completionCardIds.size
    ? Math.round((completedCardIds.size / completionCardIds.size) * 100)
    : null;
  const canReorderBinder = activeCollectionId !== "all" && !isCatalogBinder && currentDraggableIds.length > 1;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (binderPage > binderPageCount - 1) {
      setBinderPage(binderPageCount - 1);
    }
  }, [binderPage, binderPageCount]);

  useEffect(() => {
    if (filteredCards.length > 0 && activeIndex >= filteredCards.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, filteredCards.length]);

  const selectFilter = nextFilter => {
    setFilter(nextFilter);
    localStorage.setItem("cardora:binderFilter", nextFilter);
    localStorage.setItem("cardora:binderFilter:" + activeCollectionId, nextFilter);
    setActiveIndex(0);
    setBinderPage(0);
  };

  const selectCollection = nextCollectionId => {
    const collectionId = nextCollectionId || "all";
    const savedFilter = localStorage.getItem("cardora:binderFilter:" + collectionId) || "all";
    const savedViewMode = localStorage.getItem("cardora:binderViewMode:" + collectionId) || localStorage.getItem("cardora:binderViewMode") || "binder";
    setActiveCollectionId(collectionId);
    setFilter(savedFilter);
    setBinderViewMode(savedViewMode);
    localStorage.setItem("cardora:binderFilter", savedFilter);
    setActiveIndex(0);
    setBinderPage(0);
  };

  const moveCarousel = direction => {
    if (!filteredCards.length) return;
    const nextIndex = (activeIndex + direction + filteredCards.length) % filteredCards.length;
    setActiveIndex(nextIndex);
    setBinderPage(Math.floor(nextIndex / BINDER_CARDS_PER_PAGE));
    setPreviewSide("front");
    setIsPreviewZoomed(false);
  };

  const moveBinderPage = direction => {
    const nextPage = Math.min(Math.max(binderPage + direction, 0), binderPageCount - 1);
    setBinderPage(nextPage);
    setActiveIndex(nextPage * BINDER_CARDS_PER_PAGE);
  };

  const selectBinderViewMode = mode => {
    setBinderViewMode(mode);
    localStorage.setItem("cardora:binderViewMode", mode);
    localStorage.setItem("cardora:binderViewMode:" + activeCollectionId, mode);
  };

  const saveBinderOrder = async orderedCards => {
    const realCards = orderedCards.filter(card => card.id && !card.is_missing);
    await api.patch("/binder/me/order", {
      cards: realCards.map((card, index) => ({ id: card.id, slotIndex: index }))
    });
    await loadBinder();
  };

  const quickSortBinder = async sortKey => {
    if (activeCollectionId === "all" || isCatalogBinder) {
      setOrderNotice(isCatalogBinder
        ? "Las cartas de un binder por set mantienen el orden del catalogo."
        : "Selecciona un binder personalizado para guardar su orden.");
      return;
    }
    if (filter !== "all") {
      setOrderNotice("Selecciona el filtro Todas antes de ordenar el binder.");
      return;
    }

    const natural = new Intl.Collator("es", { numeric: true, sensitivity: "base" });
    const rarityOrder = new Map(catalogOptions.rarities.map((rarity, index) => [String(rarity.display_name || "").toLowerCase(), index]));
    const valueFor = (card, key) => String(card[key] || "").trim();
    const sortedCards = [...sourceCards].sort((left, right) => {
      if (sortKey === "number") return natural.compare(valueFor(left, "collector_number"), valueFor(right, "collector_number")) || natural.compare(left.name || "", right.name || "");
      if (sortKey === "type") return natural.compare(valueFor(left, "card_type") || "zzzz", valueFor(right, "card_type") || "zzzz") || natural.compare(left.name || "", right.name || "");
      if (sortKey === "rarity") return (rarityOrder.get(valueFor(left, "rarity").toLowerCase()) ?? 999) - (rarityOrder.get(valueFor(right, "rarity").toLowerCase()) ?? 999) || natural.compare(left.name || "", right.name || "");
      return natural.compare(left.name || "", right.name || "");
    });

    setActiveIndex(0);
    setBinderPage(0);
    setOrderNotice("");
    try {
      await saveBinderOrder(sortedCards);
      setOrderNotice("Binder ordenado y guardado.");
    } catch (error) {
      setOrderNotice("No pudimos guardar el orden.");
      await loadBinder();
    }
  };

  const handlePocketDragEnd = async event => {
    const { active, over } = event;
    if (!over || active.id === over.id || !canReorderBinder || isCatalogBinder) return;

    const activeUserCardId = userCardIdFromDragId(active.id);
    const overUserCardId = userCardIdFromDragId(over.id);
    const oldIndex = filteredCards.findIndex(card => card.id === activeUserCardId);
    const newIndex = filteredCards.findIndex(card => card.id === overUserCardId);
    if (oldIndex < 0 || newIndex < 0) return;

    const reorderedCards = arrayMove(filteredCards, oldIndex, newIndex);
    const realCards = reorderedCards.filter(card => card.id && !card.is_missing);
    const realCardIds = new Set(realCards.map(card => card.id));
    setCards(currentCards => {
      const untouchedCards = currentCards.filter(card => !realCardIds.has(card.id));
      const reorderedRealCards = realCards.map((card, index) => ({ ...card, slot_index: index }));
      return [...untouchedCards, ...reorderedRealCards];
    });
    setActiveIndex(newIndex);
    setOrderNotice("Orden guardado.");

    try {
      await saveBinderOrder(realCards);
    } catch (error) {
      setOrderNotice("No pudimos guardar el orden. Refrescamos el binder.");
      await loadBinder();
    }
  };

  const openAddModal = () => {
    const targetCollectionId = activeCollectionId !== "all" ? activeCollectionId : selectedCollectionId;
    const targetBinder = collections.find(collection => collection.id === targetCollectionId);
    const defaultIntention = targetBinder?.default_card_intention || "collection";
    if (activeCollectionId !== "all") setSelectedCollectionId(activeCollectionId);
    setSavedCardName("");
    setCatalogIssueType("incorrect_name");
    setCatalogIssueDetails("");
    setCatalogReportSubmitted(false);
    setCatalogReportError("");
    setBatchQueue([]);
    setAddCardMode(scannerEnabled ? "scan" : "custom");
    setScanStep("photo");
    setScanFile(null);
    setScanText("");
    setWantedText("");
    setScanResult(null);
    setScanAttemptCount(0);
    setScanNotice("");
    setSelectedScanCardId("");
    setTargetCatalogCardId("");
    setTargetSetCollectionId("");
    setIsAddingSetCard(false);
    setCustomCardForm({ name: "", setName: "", collectorNumber: "", rarity: "", cardType: "", energyType: "", cardFinish: "unspecified", language: "es" });
    setCustomRarityMode(false);
    setSelectedPhoto(null);
    setSelectedBackPhoto(null);
    setSelectedPhotoOriginal(null);
    setSelectedBackPhotoOriginal(null);
    setPhotoOptimizationInfo({ front: "", back: "" });
    setSelectedCondition("Near Mint");
    setSelectedCardIntention(defaultIntention);
    setSelectedStatus(defaultIntention === "for_trade" ? "for_trade" : "showcase");
    setFormError("");
    setShowAddModal(true);
  };

  const openMissingCardScanner = card => {
    setShowMobileCardModal(false);
    const setCollectionId = card.collection_id || activeCollectionId;
    setSelectedCollectionId(setCollectionId);
    setTargetCatalogCardId(card.catalog_card_id);
    setTargetSetCollectionId(setCollectionId);
    setIsAddingSetCard(true);
    setSavedCardName("");
    setCatalogIssueType("incorrect_name");
    setCatalogIssueDetails("");
    setCatalogReportSubmitted(false);
    setCatalogReportError("");
    setBatchQueue([]);
    setShowAddModal(true);
    setAddCardMode(scannerEnabled ? "scan" : "custom");
    setScanStep("photo");
    setScanFile(null);
    setScanText("");
    setScanResult(null);
    setScanAttemptCount(0);
    setScanNotice("Sube o escanea tu copia. La vincularemos con esta posicion del set.");
    setSelectedScanCardId("");
    setCustomCardForm({ name: card.name || "", setName: card.set_name || "", collectorNumber: card.collector_number || "", rarity: card.rarity || "", cardType: card.card_type || "", energyType: normalizeEnergyType(card.energy_type), cardFinish: card.card_finish || "unspecified", language: card.custom_language || "es" });
    setSelectedPhoto(null);
    setSelectedBackPhoto(null);
    setSelectedPhotoOriginal(null);
    setSelectedBackPhotoOriginal(null);
    setSelectedCondition("Near Mint");
    setSelectedCardIntention("collection");
    setSelectedStatus("showcase");
    setFormError("");
  };

  useEffect(() => {
    if (!showQuickCatalogSearch || quickCatalogSearch.trim().length < 2) {
      setQuickCatalogResults([]);
      setIsSearchingQuickCatalog(false);
      return undefined;
    }
    setIsSearchingQuickCatalog(true);
    const timeout = setTimeout(() => {
      api.get("/cards/search", { params: { q: quickCatalogSearch.trim(), gameSlug: "pokemon", limit: 30 } })
        .then(({ data }) => setQuickCatalogResults(Array.isArray(data) ? data : data.cards || []))
        .catch(() => setQuickCatalogResults([]))
        .finally(() => setIsSearchingQuickCatalog(false));
    }, 250);
    return () => clearTimeout(timeout);
  }, [showQuickCatalogSearch, quickCatalogSearch]);

  const openQuickCatalogAdd = (card, collectionId = activeCollectionId) => {
    setShowMobileCardModal(false);
    setQuickCatalogCard({ ...card, destinationCollectionId: collectionId });
    setQuickQuantity(1);
    setQuickAvailableQuantity(0);
    setQuickTradeEnabled(false);
    setQuickCondition("Near Mint");
    setQuickCardFinish("unspecified");
    setFormError("");
  };

  const saveQuickCatalogCard = async () => {
    if (!quickCatalogCard?.destinationCollectionId || quickCatalogCard.destinationCollectionId === "all") return;
    setIsSavingQuickCard(true);
    setFormError("");
    try {
      const automaticTradeQuantity = Math.max(0, Number(quickQuantity) - 1);
      const available = quickTradeEnabled
        ? Math.min(Number(quickQuantity), Math.max(automaticTradeQuantity, Number(quickAvailableQuantity)))
        : 0;
      const { data } = await api.post("/binder/me/cards", {
        cardId: quickCatalogCard.catalog_card_id || quickCatalogCard.id || quickCatalogCard.card_id,
        collectionId: quickCatalogCard.destinationCollectionId,
        status: available > 0 ? "for_trade" : "showcase",
        cardIntention: available > 0 ? "for_trade" : "collection",
        condition: quickCondition,
        quantity: Number(quickQuantity),
        availableQuantity: available,
        cardFinish: quickCardFinish,
        quickAdd: true,
        visibility: "public"
      });
      setQuickCatalogCard(null);
      setShowThemeSuggestionsModal(false);
      await Promise.all([loadBinder(), loadCollections()]);
      const matchMessage = Number(data.matchEvaluation?.matches || 0) > 0
        ? ` Encontramos ${data.matchEvaluation.matches} match${data.matchEvaluation.matches === 1 ? "" : "es"} y enviamos los avisos.`
        : "";
      setOrderNotice((Number(data.available_quantity) > 0
        ? `${Number(data.quantity) - Number(data.available_quantity)} copia${Number(data.quantity) - Number(data.available_quantity) === 1 ? "" : "s"} para colección y ${data.available_quantity} para intercambio.`
        : "Carta agregada usando la imagen de catalogo.") + matchMessage);
    } catch (error) {
      setFormError(error?.response?.data?.error || "No pudimos agregar esa carta.");
    } finally {
      setIsSavingQuickCard(false);
    }
  };

  const openExistingAssignment = card => {
    setShowMobileCardModal(false);
    setMissingAssignmentCard(card);
    setAssignmentSearch("");
    setPendingManualAssignmentId(null);
    setFormError("");
  };

  const assignExistingCard = async (card, force = false) => {
    if (!missingAssignmentCard || activeCollectionId === "all") return;
    setIsAssigningExisting(true);
    setFormError("");
    try {
      await api.patch(`/binder/me/cards/${card.id}/assign-catalog-slot`, {
        collectionId: activeCollectionId,
        catalogCardId: missingAssignmentCard.catalog_card_id,
        force
      });
      setMissingAssignmentCard(null);
      setOrderNotice("Carta existente vinculada al binder. Conservamos sus fotos y datos.");
      await Promise.all([loadBinder(), loadCollections()]);
    } catch (error) {
      setFormError(error?.response?.data?.error || "No pudimos vincular esa carta.");
    } finally {
      setIsAssigningExisting(false);
    }
  };

  const openThemeSuggestions = async () => {
    if (!activeCollection?.theme_type || !activeCollection?.theme_query) return;
    setShowThemeSuggestionsModal(true);
    setThemeSuggestionSetFilter("");
    setThemeSuggestionSource("mine");
    setSelectedThemeSuggestionIds([]);
    setIsLoadingThemeSuggestions(true);
    setFormError("");
    try {
      const { data } = await api.get(`/collections/me/${activeCollection.id}/theme-suggestions`, { params: { limit: 60 } });
      setThemeSuggestions(data.cards || []);
      setOwnedThemeSuggestions(data.ownedCards || []);
    } catch (error) {
      setThemeSuggestions([]);
      setOwnedThemeSuggestions([]);
      setFormError(error?.response?.data?.error || "No pudimos cargar las sugerencias del tema.");
    } finally {
      setIsLoadingThemeSuggestions(false);
    }
  };

  const activeThemeSuggestions = themeSuggestionSource === "mine" ? ownedThemeSuggestions : themeSuggestions;
  const visibleThemeSuggestions = activeThemeSuggestions.filter(card =>
    !themeSuggestionSetFilter || card.card_set_id === themeSuggestionSetFilter
  );
  const selectableThemeSuggestions = themeSuggestionSource === "mine"
    ? visibleThemeSuggestions
    : visibleThemeSuggestions.filter(card => !card.user_card_id || card.user_card_status === "wanted");
  const toggleThemeSuggestion = cardId => {
    setSelectedThemeSuggestionIds(current => current.includes(cardId)
      ? current.filter(id => id !== cardId)
      : [...current, cardId]);
  };
  const addThemeSuggestionsToOwned = async () => {
    if (!activeCollection?.id || !selectedThemeSuggestionIds.length) return;
    setIsSavingThemeSuggestionsBulk(true);
    setFormError("");
    try {
      const { data } = themeSuggestionSource === "mine"
        ? await api.post(`/collections/me/${activeCollection.id}/theme-owned-cards`, {
            userCardIds: selectedThemeSuggestionIds
          })
        : await api.post("/binder/me/cards/have-bulk", {
            collectionId: activeCollection.id,
            cardIds: selectedThemeSuggestionIds
          });
      setShowThemeSuggestionsModal(false);
      setSelectedThemeSuggestionIds([]);
      await Promise.all([loadBinder(), loadCollections()]);
      setOrderNotice(data.updated
        ? `${data.updated} carta${data.updated === 1 ? "" : "s"} ${themeSuggestionSource === "mine" ? "movida" : "agregada"}${data.updated === 1 ? "" : "s"} al binder tematico.`
        : "Las cartas seleccionadas ya estaban en este binder.");
    } catch (error) {
      setFormError(error?.response?.data?.error || "No pudimos agregar las cartas seleccionadas.");
    } finally {
      setIsSavingThemeSuggestionsBulk(false);
    }
  };

  const openBulkDuplicates = () => {
    setSelectedDuplicateCardIds([]);
    setBulkDuplicateQuantities({});
    setFormError("");
    setShowBulkDuplicatesModal(true);
  };
  const toggleDuplicateCard = cardId => {
    setSelectedDuplicateCardIds(current => current.includes(cardId)
      ? current.filter(id => id !== cardId)
      : [...current, cardId]);
  };
  const addSelectedDuplicates = async () => {
    if (!activeCollection?.id || !selectedDuplicateCardIds.length) return;
    const additions = selectedDuplicateCardIds.map(userCardId => ({
      userCardId,
      quantity: Number(bulkDuplicateQuantities[userCardId]) || 1
    }));
    setIsSavingBulkDuplicates(true);
    setFormError("");
    try {
      const { data } = await api.post("/binder/me/cards/quantity-bulk", {
        collectionId: activeCollection.id,
        cards: additions
      });
      setShowBulkDuplicatesModal(false);
      setSelectedDuplicateCardIds([]);
      setBulkDuplicateQuantities({});
      await loadBinder();
      const copiesAdded = additions.reduce((total, item) => total + item.quantity, 0);
      setOrderNotice(`${copiesAdded} copia${copiesAdded === 1 ? "" : "s"} agregada${copiesAdded === 1 ? "" : "s"} a ${data.updated} carta${data.updated === 1 ? "" : "s"} del binder.${data.tradeEnabled ? " Las copias por encima de 1 quedaron disponibles para intercambio y matches." : ""}`);
    } catch (error) {
      setFormError(error?.response?.data?.error || "No pudimos agregar las copias seleccionadas.");
    } finally {
      setIsSavingBulkDuplicates(false);
    }
  };

  const addThemeSuggestionToWanted = async card => {
    setThemeSuggestionActionId(card.id);
    try {
      await api.post("/binder/me/cards", {
        cardId: card.id,
        collectionId: activeCollection.id,
        status: "wanted",
        condition: "Buscada",
        quantity: 1,
        visibility: "public"
      });
      setThemeSuggestions(current => current.map(item => item.id === card.id ? { ...item, user_card_status: "wanted" } : item));
      setOrderNotice(`${card.name} agregada a buscadas.`);
      await loadBinder();
    } catch (error) {
      setOrderNotice(error?.response?.data?.error || "No pudimos marcar esa carta como buscada.");
    } finally {
      setThemeSuggestionActionId("");
    }
  };

  const openThemeSuggestionScanner = card => {
    setShowThemeSuggestionsModal(false);
    openMissingCardScanner({
      ...card,
      collection_id: activeCollection.id,
      catalog_card_id: card.id,
      custom_language: card.language
    });
    setScanNotice(`Sube o escanea tu ${card.name}. Usaremos los datos del catalogo y guardaremos tu foto real.`);
  };

  const openWantedCardModal = () => {
    setTargetCatalogCardId("");
    setTargetSetCollectionId("");
    setIsAddingSetCard(false);
    if (activeCollectionId !== "all") setSelectedCollectionId(activeCollectionId);
    setSavedCardName("");
    setBatchQueue([]);
    setShowAddModal(true);
    setAddCardMode("custom");
    setScanStep("details");
    setSelectedStatus("wanted");
    setSelectedCondition("Buscada");
    setSelectedPhoto(null);
    setSelectedBackPhoto(null);
    setSelectedPhotoOriginal(null);
    setSelectedBackPhotoOriginal(null);
    setPhotoOptimizationInfo({ front: "", back: "" });
    setFormError("");
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setSavedCardName("");
    setBatchQueue([]);
    setAddCardMode(scannerEnabled ? "scan" : "custom");
    setScanFile(null);
    setScanText("");
    setWantedText("");
    setScanResult(null);
    setScanAttemptCount(0);
    setScanStep("photo");
    setScanNotice("");
    setSelectedScanCardId("");
    setTargetCatalogCardId("");
    setTargetSetCollectionId("");
    setIsAddingSetCard(false);
    setCustomCardForm({ name: "", setName: "", collectorNumber: "", rarity: "", cardType: "", energyType: "", cardFinish: "unspecified", language: "es" });
    setCustomRarityMode(false);
    setSelectedStatus("showcase");
    setSelectedCondition("Near Mint");
    const rememberedCollection = localStorage.getItem("cardora:lastBinderId");
    setSelectedCollectionId(collections.some(collection => collection.id === rememberedCollection) ? rememberedCollection : collections[0]?.id || "");
    setSelectedPhoto(null);
    setSelectedVisibility("public");
    setSelectedTradeNotes("");
    setSelectedBackPhoto(null);
    setPhotoOptimizationInfo({ front: "", back: "" });
    setFormError("");
  };


  const resetCardDraft = async nextFile => {
    setSavedCardName("");
    setScanFile(nextFile || null);
    setScanText("");
    setScanResult(null);
    setScanAttemptCount(0);
    setScanStep("photo");
    setScanNotice("");
    setSelectedScanCardId("");
    setCustomCardForm({ name: "", setName: "", collectorNumber: "", rarity: "", cardType: "", energyType: "", cardFinish: "unspecified", language: "es" });
    setCustomRarityMode(false);
    setSelectedStatus("showcase");
    setSelectedCondition("Near Mint");
    setSelectedPhoto(null);
    setSelectedBackPhoto(null);
    setSelectedPhotoOriginal(null);
    setSelectedBackPhotoOriginal(null);
    setSelectedVisibility("public");
    setSelectedTradeNotes("");
    setSelectedEstimatedValue("");
    setPhotoOptimizationInfo({ front: "", back: "" });
    setAddCardMode(scannerEnabled ? "scan" : "custom");
    if (nextFile) await processPhoto(nextFile, setSelectedPhoto, "front");
  };

  const continueScanning = async () => {
    const [nextFile, ...remaining] = batchQueue;
    setBatchQueue(remaining);
    if (isAddingSetCard) {
      setIsAddingSetCard(false);
      setTargetCatalogCardId("");
      setTargetSetCollectionId("");
    }
    await resetCardDraft(nextFile || null);
  };

  const continueWithSetCard = () => {
    if (!scanFile || !selectedPhoto) {
      setFormError("Sube una foto frontal antes de continuar.");
      return;
    }
    setFormError("");
    setAddCardMode("custom");
    setScanStep("details");
    setScanNotice("Datos cargados desde el set. No hace falta analizar esta carta.");
  };

  const identifyWantedText = async () => {
    if (!wantedText.trim()) {
      setFormError("Describe la carta que buscas para sugerir datos.");
      return;
    }

    setIsIdentifyingWanted(true);
    setFormError("");
    setScanNotice("");
    try {
      const { data } = await api.post("/cards/identify-text", { text: wantedText.trim(), gameSlug: "pokemon" });
      const recognition = data.ai?.recognition;
      const suggestion = data.localSuggestion || recognition;
      if (suggestion) {
        setCustomCardForm(current => ({
          ...current,
          name: suggestion.name || current.name,
          setName: suggestion.setName || current.setName,
          collectorNumber: suggestion.collectorNumber || current.collectorNumber,
          rarity: suggestion.rarity || current.rarity,
          cardType: suggestion.cardType || current.cardType,
          energyType: normalizeEnergyType(suggestion.energyType) || current.energyType,
          language: ["es", "en", "jp", "other"].includes(suggestion.language) ? suggestion.language : current.language
        }));
        const sourceLabel = data.localSuggestion ? "BD Cardora" : "IA";
        const confidence = suggestion.confidence ? " (" + suggestion.confidence + "% confianza)" : "";
        setScanNotice("Sugerencia aplicada desde " + sourceLabel + confidence + ". Revisa antes de guardar.");
      } else {
        const aiReasonLabels = {
          ai_disabled: "La IA esta desactivada en Super Admin.",
          missing_openai_api_key: "Falta configurar la API key de OpenAI.",
          unsupported_provider: "El proveedor IA configurado no esta soportado todavia.",
          openai_error: "OpenAI devolvio un error al analizar el texto.",
          invalid_ai_json: "La IA respondio, pero no en un formato valido."
        };
        const reason = data.ai?.reason;
        setScanNotice(reason ? aiReasonLabels[reason] || `IA no usada: ${reason}.` : "No encontramos una sugerencia IA clara. Puedes cargarla manualmente.");
      }
    } catch (error) {
      setFormError(error?.response?.data?.error || "No pudimos sugerir datos para esta buscada.");
    } finally {
      setIsIdentifyingWanted(false);
    }
  };

  const runCardScan = async () => {
    if (!scanFile && !scanText.trim()) {
      setFormError("Sube una foto o escribe texto visible de la carta.");
      return;
    }
    if (scanWouldUseAi && scanAttemptCount >= 2) {
      setScanNotice("Ya usaste el analisis inicial y el unico reanalisis permitido para esta foto. Puedes cargarla manualmente o subir otra foto.");
      return;
    }
    setIsScanningCard(true);
    setFormError("");
    setScanNotice("");
    try {
      const formData = new FormData();
      if (scanFile) formData.append("image", scanFile);
      formData.append("ocrText", scanText.trim());
      formData.append("language", customCardForm.language || "unknown");
      const { data } = await api.post("/cards/scan", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setScanResult(data);
      if (scanWouldUseAi) {
        setScanAttemptCount(current => data.ai?.reason === "reanalysis_limit_reached" ? 2 : Math.min(2, current + (data.ai?.used ? 1 : 0)));
      }
      const exactCatalogMatch = Number(data.ai?.recognition?.confidence || 0) >= 95
        ? data.matches?.find(match => match.match_source === "set_number_name" && Number(match.confidence || 0) >= 95)
        : null;
      setSelectedScanCardId(exactCatalogMatch?.id || "");
      if (scanFile && !selectedPhoto) setSelectedPhoto(scanFile);
      if (data.ai?.recognition) {
        setCustomCardForm(current => ({
          ...current,
          name: targetCatalogCardId ? current.name : data.ai.recognition.name || current.name,
          setName: targetCatalogCardId ? current.setName : data.ai.recognition.setName || current.setName,
          collectorNumber: targetCatalogCardId ? current.collectorNumber : data.ai.recognition.collectorNumber || current.collectorNumber,
          rarity: data.ai.recognition.rarity || current.rarity,
          cardType: data.ai.recognition.cardType || current.cardType,
          energyType: normalizeEnergyType(data.ai.recognition.energyType) || current.energyType,
          language: ["es", "en", "jp"].includes(data.ai.recognition.language) ? data.ai.recognition.language : current.language
        }));
      }
      setAddCardMode("custom");
      setScanStep(exactCatalogMatch ? "destination" : "details");
      if (exactCatalogMatch) {
        setCustomCardForm(current => ({ ...current, name: exactCatalogMatch.name || current.name, setName: exactCatalogMatch.set_name || current.setName, collectorNumber: exactCatalogMatch.collector_number || current.collectorNumber, rarity: exactCatalogMatch.rarity || current.rarity }));
        setScanNotice("Carta identificada con coincidencia exacta de catalogo. Solo falta elegir destino y disponibilidad.");
      } else if (data.ai?.reason === "reanalysis_limit_reached") {
        setScanNotice("Limite de reanalisis alcanzado para esta foto. Reutilizamos el ultimo resultado guardado para evitar gasto extra.");
      } else if (scanWouldUseAi && data.ai?.remainingAttempts === 1) {
        setScanNotice("Analisis realizado. Te queda un unico reanalisis con IA para esta foto.");
      } else if (data.matches?.length) {
        setScanNotice("Encontramos posibles coincidencias. Selecciona una si corresponde; si ninguna es correcta, revisa los datos manualmente.");
      } else {
        setScanNotice("No hubo coincidencia de catalogo. Revisa los datos detectados o completalos manualmente.");
      }
    } catch (error) {
      setFormError(error?.response?.data?.error || "No pudimos escanear la carta.");
    } finally {
      setIsScanningCard(false);
    }
  };

  const useScanAsManualCard = async () => {
    if (!scanFile) {
      setFormError("Sube una foto primero.");
      return;
    }
    const selectedMatch = selectedScanCardId
      ? scanResult?.matches?.find(card => card.id === selectedScanCardId)
      : null;
    if (selectedMatch) {
      setCustomCardForm(current => ({
        ...current,
        name: selectedMatch.name || current.name,
        setName: selectedMatch.set_name || current.setName,
        collectorNumber: selectedMatch.collector_number || current.collectorNumber,
        rarity: selectedMatch.rarity || current.rarity
      }));
    }
    await processPhoto(scanFile, setSelectedPhoto, "front");
    setAddCardMode("custom");
    setScanStep("details");
    setFormError("");
  };

  const addCardToBinder = async card => {
    setIsSavingCard(true);
    setFormError("");

    try {
      if (addCardMode === "custom" && !customCardForm.name.trim()) {
        setFormError(selectedStatus === "wanted" ? "Indica que carta estas buscando." : "Ponle un nombre a la carta propia.");
        setIsSavingCard(false);
        return;
      }
      if (addCardMode === "custom" && selectedStatus !== "wanted" && !selectedPhoto) {
        setFormError("Sube al menos la foto frontal de tu carta.");
        setIsSavingCard(false);
        return;
      }

      const destinationCollectionId = isAddingSetCard ? targetSetCollectionId : selectedCollectionId;
      const selectedCollectionCardsCount = destinationCollectionId
        ? cards.filter(userCard => userCard.collection_id === destinationCollectionId).length
        : 0;

      if (destinationCollectionId && selectedCollectionCardsCount >= MAX_BINDER_CARDS) {
        setFormError(`Este binder ya llego al maximo de ${MAX_BINDER_PAGES} hojas (${MAX_BINDER_CARDS} cartas).`);
        return;
      }

      const resolvedCatalogCardId = targetCatalogCardId || selectedScanCardId || null;
      const { data } = await api.post("/binder/me/cards", {
        cardId: resolvedCatalogCardId,
        customName: resolvedCatalogCardId ? null : customCardForm.name.trim(),
        customSetName: resolvedCatalogCardId ? null : customCardForm.setName.trim() || null,
        customCollectorNumber: resolvedCatalogCardId ? null : customCardForm.collectorNumber.trim() || null,
        customLanguage: customCardForm.language,
        customRarity: customCardForm.rarity.trim() || null,
        customCardType: customCardForm.cardType.trim() || null,
        customEnergyType: customCardForm.energyType || null,
        cardFinish: customCardForm.cardFinish || "unspecified",
        collectionId: destinationCollectionId || null,
        status: selectedStatus,
        cardIntention: selectedStatus === "wanted" ? null : selectedCardIntention,
        condition: selectedCondition,
        quantity: 1,
        visibility: selectedVisibility,
        tradeNotes: selectedTradeNotes,
        estimatedValueCents: centsFromInput(selectedEstimatedValue),
        salePriceCents: null,
        currency: "EUR",
        tradePreferences: { mode: "cards_only" }
      });

      if (selectedPhoto) {
        const formData = new FormData();
        formData.append("photo", selectedPhoto);
        formData.append("photoType", "front");
        await api.post(`/binder/me/cards/${data.id}/photos`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      if (selectedBackPhoto) {
        const formData = new FormData();
        formData.append("photo", selectedBackPhoto);
        formData.append("photoType", "back");
        await api.post(`/binder/me/cards/${data.id}/photos`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      let feedbackWarning = "";
      if (scanResult?.scanId) {
        try {
          await api.post("/cards/scan/feedback", {
            scanId: scanResult.scanId,
            recognition: {
              tcg: "pokemon",
              name: customCardForm.name.trim(),
              setName: customCardForm.setName.trim(),
              collectorNumber: customCardForm.collectorNumber.trim(),
              rarity: customCardForm.rarity.trim(),
              cardType: customCardForm.cardType.trim(),
              energyType: customCardForm.energyType,
              language: customCardForm.language
            }
          });
        } catch (feedbackError) {
          feedbackWarning = "La carta se guardo, pero no pudimos registrar su correccion en el Data Center.";
        }
      }
      if (selectedStatus !== "wanted" && isUnknownSet) {
        try {
          const { data: suggestionData } = await api.post("/cards/catalog-suggestions/sets", {
            gameSlug: "pokemon",
            name: customCardForm.setName.trim()
          });
          if (suggestionData?.suggested) {
            feedbackWarning = [feedbackWarning, "El set fue enviado al Data Center para revision."].filter(Boolean).join(" ");
          }
        } catch {
          feedbackWarning = [feedbackWarning, "La carta se guardo, pero no pudimos sugerir el set."].filter(Boolean).join(" ");
        }
      }
      await loadBinder();
      setScanNotice(feedbackWarning);
      setSavedCardName(customCardForm.name.trim());
    } catch (error) {
      setFormError(error?.response?.data?.error || "No pudimos agregar esa carta al binder.");
    } finally {
      setIsSavingCard(false);
    }
  };

  const submitCatalogIssue = async event => {
    event.preventDefault();
    if (!targetCatalogCardId || isSubmittingCatalogReport) return;
    setIsSubmittingCatalogReport(true);
    setCatalogReportError("");
    try {
      await api.post("/cards/catalog-reports", {
        cardId: targetCatalogCardId,
        issueType: catalogIssueType,
        details: catalogIssueDetails
      });
      setCatalogReportSubmitted(true);
      setCatalogIssueDetails("");
    } catch (error) {
      setCatalogReportError(error?.response?.data?.error || "No se pudo enviar el reporte.");
    } finally {
      setIsSubmittingCatalogReport(false);
    }
  };

  const addMissingCardToWanted = async card => {
    setIsSavingCard(true);
    setBinderActionLoading("wanted");
    setFormError("");
    try {
      await api.post("/binder/me/cards", {
        cardId: card.catalog_card_id,
        collectionId: activeCollectionId,
        status: "wanted",
        condition: "Buscada",
        quantity: 1,
        visibility: "public"
      });
      await loadBinder();
      setOrderNotice("Carta agregada a buscadas. Te avisaremos si aparece una disponible.");
    } catch (error) {
      setFormError(error?.response?.data?.error || "No pudimos agregar esa carta a buscadas.");
    } finally {
      setIsSavingCard(false);
      setBinderActionLoading("");
    }
  };

  const openMissingCards = () => {
    setSelectedMissingCardIds([]);
    setFormError("");
    setShowMissingCardsModal(true);
  };
  const toggleMissingCard = cardId => {
    setSelectedMissingCardIds(current => current.includes(cardId) ? current.filter(id => id !== cardId) : [...current, cardId]);
  };
  const markMissingCardsWanted = async () => {
    if (!selectedMissingCardIds.length || activeCollectionId === "all") return;
    setIsSavingWantedBulk(true); setFormError("");
    try {
      const { data } = await api.post("/binder/me/cards/wanted-bulk", { collectionId: activeCollectionId, cardIds: selectedMissingCardIds });
      setShowMissingCardsModal(false); setSelectedMissingCardIds([]);
      await loadBinder();
      const matchMessage = Number(data.matchEvaluation?.matches || 0) > 0 ? " Encontramos " + data.matchEvaluation.matches + " match" + (data.matchEvaluation.matches === 1 ? "." : "es y enviamos los avisos.") : "";
      setOrderNotice((data.created ? data.created + " carta" + (data.created === 1 ? "" : "s") + " marcada" + (data.created === 1 ? "" : "s") + " como buscada" + (data.created === 1 ? "." : "s.") : "Las cartas seleccionadas ya estaban en tu lista.") + matchMessage);
    } catch (error) { setFormError(error?.response?.data?.error || "No pudimos actualizar tus cartas buscadas."); }
    finally { setIsSavingWantedBulk(false); }
  };
  const markMissingCardsOwned = async () => {
    if (!selectedMissingCardIds.length || activeCollectionId === "all") return;
    setIsSavingWantedBulk(true); setFormError("");
    try {
      const { data } = await api.post("/binder/me/cards/have-bulk", { collectionId: activeCollectionId, cardIds: selectedMissingCardIds });
      setShowMissingCardsModal(false); setSelectedMissingCardIds([]);
      await Promise.all([loadBinder(), loadCollections()]);
      setOrderNotice(data.updated
        ? `${data.updated} carta${data.updated === 1 ? "" : "s"} agregada${data.updated === 1 ? "" : "s"} a tu binder.`
        : "Las cartas seleccionadas ya estaban en tu binder.");
    } catch (error) {
      setFormError(error?.response?.data?.error || "No pudimos marcar las cartas como tuyas.");
    } finally {
      setIsSavingWantedBulk(false);
    }
  };

  const openEditModal = card => {
    const existingFront = preferredPhoto(card, ["front", "binder"]);
    const existingBack = preferredPhoto(card, ["back"]);
    setSelectedPhoto(null);
    setSelectedBackPhoto(null);
    setSelectedPhotoOriginal(null);
    setSelectedBackPhotoOriginal(null);
    setPhotoAdjustments({
      front: { rotation: 0, zoom: 1, offsetX: 0, offsetY: 0 },
      back: { rotation: 0, zoom: 1, offsetX: 0, offsetY: 0 }
    });
    setEditForm({
      id: card.id,
      quantity: Number(card.quantity) || 1,
      availableQuantity: Number(card.available_quantity) || 0,
      status: card.status || "showcase",
      cardIntention: card.card_intention || (card.status === "for_trade" ? "for_trade" : "collection"),
      condition: card.condition || "Near Mint",
      collectionId: card.collection_id || "",
      visibility: card.visibility || "public",
      tradeNotes: card.trade_notes || "",
      estimatedValue: card.estimated_value_cents ? String(card.estimated_value_cents / 100) : "",
      salePrice: "",
      tradeMode: "cards_only",
      photo: null,
      backPhoto: null,
      cardFinish: card.card_finish || "unspecified",
      frontImage: mediaUrl(existingFront?.photo_url || card.image_url),
      backImage: mediaUrl(existingBack?.photo_url)
    });
    setFormError("");
    setShowEditModal(true);
  };

  const setEditedPhoto = (side, file) => {
    if (side === "front") setSelectedPhoto(file);
    else setSelectedBackPhoto(file);
    setEditForm(current => ({
      ...current,
      [side === "front" ? "photo" : "backPhoto"]: file
    }));
  };

  const adjustExistingCardPhoto = async side => {
    const imageUrl = side === "front" ? editForm.frontImage : editForm.backImage;
    if (!imageUrl) return;
    setIsProcessingPhoto(true);
    setFormError("");
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error("No se pudo descargar la foto actual.");
      const blob = await response.blob();
      const extension = blob.type === "image/png" ? "png" : "jpg";
      const file = new File([blob], `cardora-${side}.${extension}`, { type: blob.type || "image/jpeg" });
      if (side === "front") setSelectedPhotoOriginal(file);
      else setSelectedBackPhotoOriginal(file);
      const baseAdjustment = { rotation: 0, zoom: 1, offsetX: 0, offsetY: 0 };
      setPhotoAdjustments(current => ({ ...current, [side]: baseAdjustment }));
      await applyPhotoAdjustment(file, adjusted => setEditedPhoto(side, adjusted), side, baseAdjustment);
      setAdjustingPhotoSide(side);
    } catch (error) {
      setFormError(error.message || "No pudimos preparar la foto para editarla.");
    } finally {
      setIsProcessingPhoto(false);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setFormError("");
  };

  const saveBinderCard = async event => {
    event.preventDefault();
    setIsSavingCard(true);
    setFormError("");

    try {
      await api.patch(`/binder/me/cards/${editForm.id}`, {
        status: editForm.status,
        cardIntention: editForm.cardIntention,
        condition: editForm.condition,
        cardFinish: editForm.cardFinish,
        quantity: Number(editForm.quantity),
        availableQuantity: Math.min(Number(editForm.quantity), Number(editForm.availableQuantity)),
        collectionId: editForm.collectionId || null,
        visibility: editForm.visibility,
        tradeNotes: editForm.tradeNotes,
        estimatedValueCents: centsFromInput(editForm.estimatedValue),
        salePriceCents: null,
        currency: "EUR",
        tradePreferences: { mode: "cards_only" }
      });

      if (editForm.photo) {
        const formData = new FormData();
        formData.append("photo", editForm.photo);
        formData.append("photoType", "front");
        await api.post(`/binder/me/cards/${editForm.id}/photos`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      if (editForm.backPhoto) {
        const formData = new FormData();
        formData.append("photo", editForm.backPhoto);
        formData.append("photoType", "back");
        await api.post(`/binder/me/cards/${editForm.id}/photos`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      await loadBinder();
      closeEditModal();
    } catch (error) {
      setFormError("No pudimos actualizar esa carta.");
    } finally {
      setIsSavingCard(false);
    }
  };

  const frontPhoto = activeCard ? preferredPhoto(activeCard, ["front", "binder"]) : null;
  const backPhoto = activeCard ? preferredPhoto(activeCard, ["back"]) : null;
  const frontImage = mediaUrl(frontPhoto?.photo_url || activeCard?.image_url);
  const backImage = backPhoto ? mediaUrl(backPhoto.photo_url) : null;
  const previousCardImage = filteredCards[activeIndex - 1] ? mediaUrl(cardFrontImage(filteredCards[activeIndex - 1])) : "";
  const nextCardImage = filteredCards[activeIndex + 1] ? mediaUrl(cardFrontImage(filteredCards[activeIndex + 1])) : "";

  useEffect(() => {
    setPreviewSide("front");
    setIsPreviewZoomed(false);
    setPreviewZoomOrigin({ x: 50, y: 50 });
    [previousCardImage, nextCardImage].filter(Boolean).forEach(src => {
      const image = new Image();
      image.src = src;
    });
  }, [activeCard?.id, activeCard?.catalog_card_id, previousCardImage, nextCardImage]);

  useEffect(() => {
    setIsPreviewZoomed(false);
    setPreviewZoomOrigin({ x: 50, y: 50 });
  }, [previewSide]);

  useEffect(() => {
    if (!showAddModal || selectedStatus !== "wanted" || wantedText.trim().length < 2) {
      setWantedRealMatches([]);
      setWantedCatalogMatches([]);
      setIsSearchingWanted(false);
      return undefined;
    }

    setIsSearchingWanted(true);
    const timeout = setTimeout(() => {
      Promise.all([
        api.get("/binder/cards/search-real", { params: { q: wantedText.trim(), limit: 8 } }),
        api.get("/cards/search", { params: { q: wantedText.trim(), gameSlug: "pokemon", limit: 8 } })
      ])
        .then(([realResponse, catalogResponse]) => { setWantedRealMatches(realResponse.data || []); setWantedCatalogMatches(catalogResponse.data || []); })
        .catch(() => { setWantedRealMatches([]); setWantedCatalogMatches([]); })
        .finally(() => setIsSearchingWanted(false));
    }, 300);

    return () => clearTimeout(timeout);
  }, [showAddModal, selectedStatus, wantedText]);

  const rotatePreview = () => {
    if (!backImage) {
      setPreviewSide("front");
      return;
    }
    setPreviewSide(current => (current === "front" ? "back" : "front"));
  };

  const movePreviewZoom = event => {
    if (!isPreviewZoomed) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((event.clientX - bounds.left) / bounds.width) * 100));
    const y = Math.max(0, Math.min(100, ((event.clientY - bounds.top) / bounds.height) * 100));
    setPreviewZoomOrigin({ x, y });
  };

  const togglePreviewZoom = () => {
    setIsPreviewZoomed(current => {
      if (current) setPreviewZoomOrigin({ x: 50, y: 50 });
      return !current;
    });
  };

  const copyMyBinderLink = async (tab = "binder") => {
    const profileKey = currentUser?.handle || currentUser?.id;
    if (!profileKey) return;
    const params = new URLSearchParams();
    if (tab !== "posts") params.set("tab", tab);
    if (activeCollectionId !== "all" && tab === "binder") params.set("collection", activeCollectionId);
    const query = params.toString();
    const isScopedBinderShare = activeCollectionId !== "all" && ["binder", "duplicates", "trade", "wanted"].includes(tab);
    const url = isScopedBinderShare
      ? `${window.location.origin}/binder/${profileKey}/${activeCollectionId}${tab === "binder" ? "" : `?view=${tab}`}`
      : `${window.location.origin}/u/${profileKey}?${query}`;
    const ownerName = currentUser?.display_name || currentUser?.name || currentUser?.handle || "Coleccionista";
    const binderName = activeCollection?.name || "Todos mis binders";
    const title = tab === "trade"
      ? `Cartas para trade de ${ownerName}`
      : tab === "wanted"
        ? `Cartas buscadas por ${ownerName}`
        : `${binderName} de ${ownerName}`;
    const text = `${title} en Cardora`;

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        setOrderNotice("Binder compartido.");
        setShowShareModal(false);
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }

    await navigator.clipboard.writeText(url);
    setOrderNotice("Link copiado.");
    setShowShareModal(false);
  };

  const createCollection = async event => {
    event.preventDefault();
    setFormError("");
    if (!collectionForm.binderStructure) {
      setFormError("Selecciona si quieres organizarlo por set o personalizado.");
      setCollectionStep(1);
      return;
    }
    if (collectionForm.binderStructure === "custom" && !collectionForm.name.trim()) {
      setFormError("Ponle un nombre al binder personalizado.");
      setCollectionStep(2);
      return;
    }
    if (collectionForm.binderStructure === "custom" && collectionForm.themeType && !collectionForm.themeQuery.trim()) {
      setFormError("Indica que tema quieres coleccionar.");
      setCollectionStep(2);
      return;
    }
    if (collectionForm.binderStructure === "set" && !collectionForm.cardSetId) {
      setFormError("Selecciona un set del catalogo.");
      setCollectionStep(2);
      return;
    }

    setIsSavingCollection(true);
    try {
      const payload = {
        name: collectionForm.name,
        collectionType: collectionForm.binderStructure === "set" ? "set" : collectionForm.binderIntent === "trade" ? "trade" : "custom",
        binderIntent: collectionForm.binderIntent,
        binderStructure: collectionForm.binderStructure,
        defaultCardIntention: collectionForm.binderIntent === "trade" ? "for_trade" : collectionForm.defaultCardIntention,
        cardSetId: collectionForm.binderStructure === "set" ? collectionForm.cardSetId || null : null,
        visibility: collectionForm.visibility,
        targetTotal: Number(collectionForm.targetTotal) || null,
        description: collectionForm.description,
        themeType: collectionForm.binderStructure === "custom" ? collectionForm.themeType || null : null,
        themeQuery: collectionForm.binderStructure === "custom" ? collectionForm.themeQuery.trim() || null : null
      };
      const { data } = editingCollectionId
        ? await api.patch(`/collections/me/${editingCollectionId}`, payload)
        : await api.post("/collections/me", payload);
      await loadCollections();
      await loadBinder();
      setFilter("all");
      setActiveCollectionId(data.id);
      setSelectedCollectionId(data.id);
      setShowCollectionModal(false);
      setEditingCollectionId(null);
      setCollectionForm(emptyCollectionForm());
      if (!editingCollectionId && activationProgress?.enabled) {
        setActivationParams({ collection: data.id, action: data.card_set_id || data.binder_structure === "set" ? "mark-owned" : "add-card" }, { replace: true });
      }
      setOrderNotice(editingCollectionId
        ? "Binder actualizado."
        : data.binder_structure === "set"
          ? "Binder creado. Selecciona las cartas que tienes; no necesitas fotos."
          : "Binder creado. Usa Agregar para sumar tu primera carta.");
    } catch (error) {
      setFormError(editingCollectionId ? "No pudimos actualizar ese binder." : "No pudimos crear ese binder.");
    } finally {
      setIsSavingCollection(false);
    }
  };

  const openCreateCollectionModal = () => {
    setEditingCollectionId(null);
    setFormError("");
    setCollectionForm({ ...emptyCollectionForm(), binderStructure: "" });
    setCollectionStep(1);
    setShowCollectionModal(true);
  };

  const closeBinderGuide = () => {
    localStorage.setItem("cardora:binderGuideSeen", "true");
    setShowBinderGuide(false);
  };

  const startGuidedBinder = structure => {
    void api.post("/analytics/events", { eventType: "binder_guide_completed", metadata: { structure } }).catch(() => undefined);
    localStorage.setItem("cardora:binderGuideSeen", "true");
    setShowBinderGuide(false);
    setEditingCollectionId(null);
    setFormError("");
    const selectedSet = structure === "set" ? catalogOptions.sets.find(set => set.id === binderGuideSetId) : null;
    setCollectionForm({
      ...emptyCollectionForm(),
      binderStructure: structure,
      cardSetId: selectedSet?.id || "",
      catalogLanguage: selectedSet?.language || "es",
      name: selectedSet?.name || "",
      targetTotal: baseSetTarget(selectedSet)
    });
    setCollectionStep(2);
    setShowCollectionModal(true);
  };

  const openEditCollectionModal = () => {
    if (!activeCollection) return;
    setEditingCollectionId(activeCollection.id);
    setFormError("");
    setCollectionForm({
      name: activeCollection.name || "",
      collectionType: activeCollection.collection_type || "custom",
      binderIntent: activeCollection.binder_intent || (activeCollection.collection_type === "trade" ? "trade" : "collection"),
      binderStructure: activeCollection.binder_structure || (activeCollection.collection_type === "set" ? "set" : "custom"),
      defaultCardIntention: activeCollection.default_card_intention || (activeCollection.collection_type === "trade" ? "for_trade" : "collection"),
      cardSetId: activeCollection.card_set_id || "",
      catalogLanguage: activeCollection.set_language || catalogOptions.sets.find(set => set.id === activeCollection.card_set_id)?.language || "es",
      visibility: activeCollection.visibility || "public",
      targetTotal: activeCollection.target_total || "",
      description: activeCollection.description || "",
      themeType: activeCollection.theme_type || "",
      themeQuery: activeCollection.theme_query || ""
    });
    setCollectionStep(1);
    setShowCollectionModal(true);
  };

  const deleteCollection = async () => {
    if (!activeCollection) return;
    setFormError("");
    try {
      const { data } = await api.delete("/collections/me/" + activeCollection.id, { data: collectionDeleteForm });
      setActiveCollectionId("all");
      setPendingDeleteCollectionId(null);
      await loadCollections();
      await loadBinder();
      const messages = {
        keep: "Binder borrado. " + (data.cards_count || 0) + " cartas quedaron disponibles sin binder.",
        move: "Binder borrado. " + (data.cards_count || 0) + " cartas se movieron al binder elegido.",
        delete: "Binder y " + (data.cards_count || 0) + " cartas borrados definitivamente."
      };
      setOrderNotice(messages[data.card_action] || "Binder borrado.");
    } catch (error) {
      setFormError(error?.response?.data?.error || "No pudimos borrar el binder.");
    }
  };
  const deleteBinderCard = async card => {
    if (!card?.id) return;
    await api.delete(`/binder/me/cards/${card.id}`);
    setActiveIndex(0);
    setPendingDeleteCardId(null);
    await loadBinder();
    setOrderNotice("Carta borrada del binder.");
  };


  useEffect(() => {
    if (!collectionsLoaded || binderLoading || !activationProgress?.enabled) return;
    const action = activationParams.get("action");
    const requestedCollection = activationParams.get("collection");
    if (!action) return;
    if (requestedCollection && collections.some(collection => collection.id === requestedCollection) && activeCollectionId !== requestedCollection) {
      setActiveCollectionId(requestedCollection);
      return;
    }
    if (action === "choose-set") {
      setEditingCollectionId(null);
      setCollectionForm({ ...emptyCollectionForm(), binderStructure: "set" });
      setCollectionStep(2);
      setShowCollectionModal(true);
    } else if (action === "share") {
      setShowShareModal(true);
    } else if (["mark-owned", "mark-duplicates", "add-card"].includes(action)) {
      if (!activeCollection && collections.length) {
        setActiveCollectionId(collections.find(collection => collection.card_set_id)?.id || collections[0].id);
        return;
      }
      if (!activeCollection) {
        setBinderGuideStep(1);
        setShowBinderGuide(true);
      } else if (action === "mark-owned" && isSetBinder) {
        setSelectedMissingCardIds([]);
        setShowMissingCardsModal(true);
      } else if (action === "mark-duplicates") {
        setSelectedDuplicateCardIds([]);
        setBulkDuplicateQuantities({});
        setShowBulkDuplicatesModal(true);
      } else {
        setShowQuickCatalogSearch(true);
      }
    }
    const nextParams = new URLSearchParams(activationParams);
    nextParams.delete("action");
    setActivationParams(nextParams, { replace: true });
    // Consume each action once, after the selected binder has loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activationParams, activationProgress?.enabled, collectionsLoaded, binderLoading, activeCollectionId, activeCollection, collections, isSetBinder, setActivationParams]);

  useEffect(() => {
    if (!activationProgress?.enabled) return;
    let cancelled = false;
    api.get("/users/me/onboarding-progress")
      .then(({ data }) => { if (!cancelled) setActivationProgress(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [cards, collections, activationProgress?.enabled]);

  useFeedbackMessage(orderNotice, orderNotice.startsWith("No") ? "warning" : "success");

  if (binderLoading || !collectionsLoaded) return <Container fluid className="page-band binder-page px-3 px-lg-4"><BinderSkeleton /></Container>;

  return (
    <Container fluid className="page-band binder-page px-3 px-lg-4">
      {activationProgress?.enabled && <ActivationGuide progress={activationProgress} />}
      {showBinderOnboarding && !activationProgress?.enabled && (
        <section className="binder-onboarding-strip" aria-label="Primeros pasos">
          <div><span className="binder-onboarding-icon"><i className="bi bi-award-fill" /></span><div><strong>Prepara tu primer binder</strong><small>Completa estos pasos para desbloquear tu medalla de coleccionista.</small></div></div>
          <div className="binder-onboarding-steps">
            <button type="button" className={collections.length ? "is-complete" : ""} onClick={() => { setBinderGuideStep(1); setShowBinderGuide(true); }}><i className={`bi ${collections.length ? "bi-check-circle-fill" : "bi-circle"}`} />Guia inicial</button>
            <button type="button" className={ownedCardsCount ? "is-complete" : ""} onClick={() => activeCollectionId === "all" ? openAddModal() : setShowQuickCatalogSearch(true)}><i className={`bi ${ownedCardsCount ? "bi-check-circle-fill" : "bi-circle"}`} />Agregar carta</button>
            <button type="button" onClick={() => { setBinderGuideStep(3); setShowBinderGuide(true); }}><i className="bi bi-stars" />Descubrir acciones</button>
            <button type="button" onClick={() => copyMyBinderLink("binder")}><i className="bi bi-share" />Compartir</button>
          </div>
          <button type="button" className="binder-onboarding-dismiss" aria-label="Ocultar primeros pasos" onClick={() => { setShowBinderOnboarding(false); localStorage.setItem("cardora:binderOnboardingDismissed", "true"); }}><i className="bi bi-x-lg" /></button>
        </section>
      )}

      <Modal show={showBinderGuide} onHide={closeBinderGuide} centered size="lg" contentClassName="binder-guide-modal">
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-journal-album me-2" />Crea tu primer binder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="binder-guide-progress" aria-label={`Paso ${binderGuideStep} de 3`}>
            {(binderGuideChoice === "set" ? ["Elige", "Completa", "Gestiona"] : ["Elige", "Agrega", "Conecta"]).map((label, index) => {
              const step = index + 1;
              return <button type="button" key={label} className={`${step <= binderGuideStep ? "is-active" : ""} ${step === binderGuideStep ? "is-current" : ""}`} onClick={() => setBinderGuideStep(step)}><b>{step < binderGuideStep ? <i className="bi bi-check-lg" /> : step}</b><small>{label}</small></button>;
            })}
          </div>
          {binderGuideStep === 1 && <div className="binder-guide-content">
            <span className="binder-guide-kicker">Paso 1</span>
            <h2>Como quieres empezar?</h2>
            <p>Elige una estructura. Podras cambiar el nombre y los detalles antes de crearla.</p>
            <div className="binder-guide-paths binder-guide-choice-grid">
              <button type="button" className={binderGuideChoice === "set" ? "is-selected" : ""} onClick={() => setBinderGuideChoice("set")}><i className="bi bi-layers" /><strong>Completar un set</strong><span>Cartas ordenadas y faltantes visibles</span><em>Recomendado</em></button>
              <button type="button" className={binderGuideChoice === "custom" ? "is-selected" : ""} onClick={() => setBinderGuideChoice("custom")}><i className="bi bi-grid-3x3-gap" /><strong>Binder personalizado</strong><span>Personaje, favoritos o intercambio</span></button>
            </div>
            {binderGuideChoice === "set" && <div className="binder-guide-set-picker">
              <div className="binder-guide-language" role="group" aria-label="Idioma del set">
                {[["es", "Español"], ["en", "Inglés"]].map(([value, label]) => (
                  <button type="button" key={value} className={binderGuideLanguage === value ? "is-selected" : ""} onClick={() => {
                    setBinderGuideLanguage(value);
                    setBinderGuideSetId("");
                    setBinderGuideSetSearch("");
                  }}>{label}</button>
                ))}
              </div>
              <div className="binder-set-search-control">
                <i className="bi bi-search" />
                <Form.Control value={binderGuideSetSearch} onChange={event => setBinderGuideSetSearch(event.target.value)} placeholder="Busca un set por nombre o codigo" autoComplete="off" />
                {binderGuideSetSearch && <button type="button" onClick={() => setBinderGuideSetSearch("")} aria-label="Limpiar busqueda"><i className="bi bi-x-lg" /></button>}
              </div>
              <div className="binder-guide-set-grid">
                {binderGuideSets.map(set => {
                  const selected = binderGuideSetId === set.id;
                  return <button type="button" key={set.id} className={selected ? "is-selected" : ""} onClick={() => setBinderGuideSetId(set.id)}>
                    <span><CatalogSetImage set={set} /></span>
                    <strong>{set.name}</strong>
                    <small>{[set.code, set.total_cards ? set.total_cards + " cartas" : ""].filter(Boolean).join(" / ")}</small>
                    <i className={"bi " + (selected ? "bi-check-circle-fill" : "bi-circle")} />
                  </button>;
                })}
              </div>
              {!binderGuideSets.length && <small className="binder-guide-set-empty">No encontramos sets publicados con esa busqueda.</small>}
              <small className="binder-guide-set-note">Puedes elegirlo ahora o continuar y hacerlo en el siguiente paso.</small>
            </div>}
          </div>}
          {binderGuideStep === 2 && <div className="binder-guide-content">
            <span className="binder-guide-kicker">Paso 2</span>
            <h2>{binderGuideChoice === "set" ? "Marca las cartas que ya tienes" : "Agrega tu primera carta"}</h2>
            <p>{binderGuideChoice === "set" ? <>Dentro del binder, abre <strong>Marcar cartas que tengo</strong>, selecciona varias y confirma. No necesitas subir fotos.</> : <>Usa <strong>Agregar</strong> para buscar una carta o <strong>Foto</strong> para escanearla. Podras completar sus datos antes de guardarla.</>}</p>
            {binderGuideChoice === "set" && <div className="binder-guide-current-action"><i className="bi bi-check2-square" /><span><strong>Marcar cartas que tengo</strong><small>Seleccion multiple desde el catalogo completo del set</small></span></div>}
            <div className="binder-guide-demo" aria-label="Demostracion de una pagina de binder">
              {[0, 1, 2, 3, 4, 5].map(index => {
                const guideCard = binderGuideCards[index];
                const isGuideOwned = String(guideCard?.id || index) === String(binderGuideOwnedCardId);
                return <div key={guideCard?.id || index} className={`binder-guide-demo-item ${isGuideOwned ? "is-owned" : ""} ${!guideCard ? "is-placeholder" : ""}`}><button type="button" className="binder-guide-demo-card" disabled={!guideCard} onClick={() => setBinderGuidePreviewCard(guideCard)} aria-label={`Abrir ${guideCard?.name || "carta"}`}>{guideCard ? <img src={mediaUrl(guideCard.image_url)} alt={guideCard.name} /> : <i className={`bi ${isLoadingBinderGuideCards ? "bi-hourglass-split" : "bi-card-image"}`} />}{isGuideOwned && <span className="binder-guide-owned-check"><i className="bi bi-check-lg" /></span>}</button>{index === 1 && !isGuideOwned && <small className="binder-guide-card-hint">Toca para abrir</small>}</div>;
              })}
            </div>
            <div className={`binder-guide-feedback ${binderGuideOwnedCardId ? "is-visible" : ""}`}><i className="bi bi-check-circle-fill" /> Listo. La carta queda en tu binder sin obligarte a subir una foto.</div>
          </div>}
          {binderGuideStep === 3 && <div className="binder-guide-content">
            <span className="binder-guide-kicker">Paso 3</span>
            <h2>{binderGuideChoice === "set" ? "Gestiona faltantes y repetidas" : "Haz que Cardora busque por ti"}</h2>
            <p>{binderGuideChoice === "set" ? <>Las cartas que faltan ya aparecen <strong>en gris</strong>. Estas acciones son opcionales y puedes usarlas cuando las necesites.</> : <>Marca una carta como <strong>buscada</strong> para recibir coincidencias cuando alguien la tenga disponible.</>}</p>
            <div className="binder-guide-action-list">
              {binderGuideChoice === "set" && <div className="binder-guide-action-item"><i className="bi bi-copy" /><span><strong>Marcar repetidas</strong><small>Indica cantidades; las copias adicionales pueden quedar disponibles para intercambio.</small></span></div>}
              <div className="binder-guide-action-item is-wanted"><i className="bi bi-search-heart" /><span><strong>Marcar como buscadas</strong><small>Solo para las faltantes que realmente buscas. Esto activa matches y avisos.</small></span></div>
              <div className="binder-guide-action-item is-match"><i className="bi bi-stars" /><span><strong>Coincidencias automaticas</strong><small>Cardora compara tus busquedas con cartas disponibles de otros coleccionistas.</small></span></div>
            </div>
          </div>}
        </Modal.Body>
        <Modal.Footer>
          {binderGuideStep > 1 ? <Button variant="outline-dark" onClick={() => setBinderGuideStep(step => step - 1)}>Atras</Button> : <Button variant="link" className="text-muted" onClick={closeBinderGuide}>Ahora no</Button>}
          {binderGuideStep < 3 && <Button className="btn-cardora" onClick={() => setBinderGuideStep(step => step + 1)}>Siguiente <i className="bi bi-arrow-right" /></Button>}
          {binderGuideStep === 3 && <Button className="btn-cardora" onClick={() => startGuidedBinder(binderGuideChoice)}><i className="bi bi-plus-lg me-2" />Crear mi binder</Button>}
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(binderGuidePreviewCard)} onHide={() => setBinderGuidePreviewCard(null)} centered contentClassName="binder-guide-card-modal">
        <Modal.Header closeButton><Modal.Title>Detalle de la carta</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="binder-guide-card-detail">
            {binderGuidePreviewCard?.image_url && <img src={mediaUrl(binderGuidePreviewCard.image_url)} alt={binderGuidePreviewCard.name} />}
            <div><strong>{binderGuidePreviewCard?.name}</strong><span>{[binderGuidePreviewCard?.set_name, binderGuidePreviewCard?.collector_number].filter(Boolean).join(" - ")}</span><small>Puedes empezar con la imagen del catalogo y subir tu foto real mas adelante.</small></div>
          </div>
          <div className="binder-guide-action-callout"><i className="bi bi-hand-index-thumb" /><span>Esto es una demostracion. Selecciona <strong>La tengo</strong></span></div>
          <BinderQuickActions
            className="binder-guide-real-actions is-focus-have"
            autoFocusAction="have"
            onHave={() => { setBinderGuideOwnedCardId(String(binderGuidePreviewCard?.id || "")); setBinderGuidePreviewCard(null); }}
            onWant={binderGuideChoice === "custom" ? () => { setBinderGuidePreviewCard(null); setBinderGuideStep(3); } : undefined}
            onPhoto={() => { setBinderGuideOwnedCardId(String(binderGuidePreviewCard?.id || "")); setBinderGuidePreviewCard(null); }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-dark" onClick={() => setBinderGuidePreviewCard(null)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>

      <section className="binder-context-nav" aria-label="Navegacion del binder">
        <div className={`binder-context-primary ${activeCollection ? "has-active-binder" : "is-binder-index"}`}>
          <BinderSwitcher
            collections={collections}
            activeId={activeCollectionId}
            totalOwned={allBindersOwnedCount}
            sets={catalogOptions.sets}
            onSelect={selectCollection}
            onCreate={openCreateCollectionModal}
            renderSetImage={set => <CatalogSetImage key={set.id} set={set} />}
          />

          {activeCollection && <div className="binder-context-summary">
            <strong>{activeCollection.name}</strong>
            <span>
              {isCatalogBinder
                ? `${completedCardIds.size}/${completionCardIds.size} cartas - ${completion}% completado`
                : `${ownedCardsCount} cartas${ownedCopiesCount !== ownedCardsCount ? ` - ${ownedCopiesCount} copias` : ""}`}
            </span>
            <span className="binder-view-count"><i className="bi bi-eye me-1" />{activeCollection.views_count || 0} vistas</span>
            {isCatalogBinder && <div className="binder-context-progress" aria-label={`${completion}% completado`}><span style={{ width: `${completion}%` }} /></div>}
            {isSetBinder && <div className="binder-bulk-set-actions"><Button
              className="binder-bulk-have-button"
              disabled={!missingSetCards.length}
              onClick={openMissingCards}
            >
              <i className="bi bi-check2-square" />
              <span>Marcar cartas que tengo</span>
              <Badge bg="light" text="dark">{missingSetCards.length}</Badge>
            </Button><Button variant="outline-dark" className="binder-bulk-have-button is-secondary" disabled={!bulkDuplicateCards.length} onClick={openBulkDuplicates}><i className="bi bi-copy" /><span>Marcar repetidas</span><Badge bg="light" text="dark">{bulkDuplicateCards.length}</Badge></Button></div>}
          </div>}

          {activeCollection && <div id="binder-primary-actions" className="binder-context-primary-actions is-expanded" aria-label="Acciones del binder">
            <Button className="btn-cardora binder-action-button" onClick={() => setShowQuickCatalogSearch(true)} title={isSetBinder ? "Añadir cartas al binder" : "Agregar carta"}><i className="bi bi-plus-lg" /><span>{isSetBinder ? "Añadir al binder" : "Agregar"}</span></Button>
            {isCustomBinder && <>
              <Button variant="outline-dark" className="binder-action-button" onClick={openBulkDuplicates} disabled={!bulkDuplicateCards.length} title="Agregar copias repetidas"><i className="bi bi-copy" /><span>Repetidas</span></Button>
              <Button variant="outline-dark" className="binder-action-button" onClick={openAddModal} title="Escanear carta"><i className="bi bi-camera-fill" /><span>Foto</span></Button>
              <Button variant="outline-dark" className="binder-action-button" onClick={openWantedCardModal} title="Agregar carta buscada"><i className="bi bi-search-heart" /><span>Buscada</span></Button>
            </>}
            <Button variant="outline-dark" className="binder-action-button" onClick={() => setShowShareModal(true)} title="Compartir binder"><i className="bi bi-share" /><span>Compartir</span></Button>
            <Button variant="outline-dark" className="binder-action-button" onClick={openEditCollectionModal} title="Editar binder"><i className="bi bi-pencil-square" /><span>Editar</span></Button>
            <Button variant="outline-danger" className="binder-action-button" onClick={() => { setFormError(""); setCollectionDeleteForm({ cardAction: "keep", targetCollectionId: "" }); setPendingDeleteCollectionId(activeCollection.id); }} title="Borrar binder"><i className="bi bi-trash" /><span>Borrar</span></Button>
          </div>}
        </div>

        {activeCollection && <div className="binder-context-secondary binder-controls-desktop">
          <div className="binder-filter-tabs" role="tablist" aria-label="Filtrar cartas">
            {Object.entries(statusLabels).map(([status, label]) => (
              <button type="button" role="tab" aria-selected={filter === status} className={filter === status ? "is-active" : ""} key={status} onClick={() => selectFilter(status)}>
                <span>{label}</span><small>{filterCounts[status]}</small>
              </button>
            ))}
          </div>
          <div className="binder-context-tools">
            <div className="binder-view-switch" aria-label="Vista del binder">
              <button type="button" className={binderViewMode === "binder" ? "is-active" : ""} onClick={() => selectBinderViewMode("binder")} title="Vista binder"><i className="bi bi-grid-3x3-gap" /><span>Binder</span></button>
              <button type="button" className={binderViewMode === "list" ? "is-active" : ""} onClick={() => selectBinderViewMode("list")} title="Vista lista"><i className="bi bi-list-ul" /><span>Lista</span></button>
            </div>
            {!isCatalogBinder && activeCollectionId !== "all" && <Dropdown><Dropdown.Toggle variant="outline-dark" size="sm"><i className="bi bi-sort-down me-1" />Ordenar</Dropdown.Toggle><Dropdown.Menu><Dropdown.Item onClick={() => quickSortBinder("number")}>Por numero</Dropdown.Item><Dropdown.Item onClick={() => quickSortBinder("type")}>Por tipo</Dropdown.Item><Dropdown.Item onClick={() => quickSortBinder("rarity")}>Por rareza</Dropdown.Item><Dropdown.Item onClick={() => quickSortBinder("name")}>Por nombre</Dropdown.Item></Dropdown.Menu></Dropdown>}
            {isSetBinder && <Button variant="outline-dark" size="sm" onClick={openMissingCards}><i className="bi bi-list-check me-1" />Faltantes ({missingSetCards.length})</Button>}
            {activeCollection?.theme_query && <Button variant="outline-dark" size="sm" onClick={openThemeSuggestions}><i className="bi bi-stars me-1" />Cartas del tema</Button>}
          </div>
        </div>}
        {activeCollection && <div className="binder-controls-mobile">
          <Form.Select size="sm" value={filter} onChange={event => selectFilter(event.target.value)} aria-label="Filtrar cartas">
            {Object.entries(statusLabels).map(([status, label]) => <option value={status} key={status}>{label} ({filterCounts[status]})</option>)}
          </Form.Select>
          <div className="binder-view-switch" aria-label="Vista del binder">
            <button type="button" className={binderViewMode === "binder" ? "is-active" : ""} onClick={() => selectBinderViewMode("binder")} aria-label="Vista binder"><i className="bi bi-grid-3x3-gap" /></button>
            <button type="button" className={binderViewMode === "list" ? "is-active" : ""} onClick={() => selectBinderViewMode("list")} aria-label="Vista lista"><i className="bi bi-list-ul" /></button>
          </div>
          <Dropdown align="end">
            <Dropdown.Toggle variant="outline-dark" size="sm" className="binder-mobile-tools-toggle" aria-label="Herramientas"><i className="bi bi-sliders2" /></Dropdown.Toggle>
            <Dropdown.Menu>
              {!isCatalogBinder && activeCollectionId !== "all" && <><Dropdown.Header>Ordenar</Dropdown.Header><Dropdown.Item onClick={() => quickSortBinder("number")}>Por numero</Dropdown.Item><Dropdown.Item onClick={() => quickSortBinder("type")}>Por tipo</Dropdown.Item><Dropdown.Item onClick={() => quickSortBinder("rarity")}>Por rareza</Dropdown.Item><Dropdown.Item onClick={() => quickSortBinder("name")}>Por nombre</Dropdown.Item></>}
              {isSetBinder && <Dropdown.Item onClick={openMissingCards}><i className="bi bi-list-check me-2" />Faltantes ({missingSetCards.length})</Dropdown.Item>}
              {activeCollection?.theme_query && <Dropdown.Item onClick={openThemeSuggestions}><i className="bi bi-stars me-2" />Cartas del tema</Dropdown.Item>}
            </Dropdown.Menu>
          </Dropdown>
        </div>}
      </section>

      <Row className="g-3 align-items-start binder-workspace">
        <Col xl={4} className="binder-desktop-preview">
          <section className="binder-feature">
            {activeCard ? (
              <>
                <BinderCardViewer
                  name={activeCard.name}
                  frontImage={frontImage}
                  backImage={backImage}
                  side={previewSide}
                  onRotate={rotatePreview}
                  isZoomed={isPreviewZoomed}
                  zoomOrigin={previewZoomOrigin}
                  onMoveZoom={movePreviewZoom}
                  onResetZoom={() => {
                    setIsPreviewZoomed(false);
                    setPreviewZoomOrigin({ x: 50, y: 50 });
                  }}
                  onToggleZoom={togglePreviewZoom}
                  isMissing={activeCardIsVisuallyMissing}
                />
                <div className="binder-feature-meta">
                  <Badge bg={activeCard.status === "for_trade" ? "warning" : "success"} text={activeCard.status === "for_trade" ? "dark" : undefined}>
                    {statusLabels[activeCard.status] || activeCard.status || "Catalogo"}
                  </Badge>
                  <h2>{activeCard.name}</h2>
                  <p>{activeCard.set_name} - {activeCard.collector_number}</p>
                  <div className="binder-stat-row">
                    <RarityMark rarity={activeCard.rarity} options={catalogOptions.rarities} />
                    {activeCard.card_finish && activeCard.card_finish !== "unspecified" && <span>{cardFinishLabel(activeCard.card_finish)}</span>}
                    <span>{activeCard.card_type || "Sin tipo"}</span>
                    {!frontPhoto && activeCard.id && <span>Imagen de catalogo</span>}
                    {activeCard.id && <span>{activeCard.quantity || 1} {(activeCard.quantity || 1) === 1 ? "copia" : "copias"}</span>}
                    {Number(activeCard.available_quantity) > 0 && <span>{activeCard.available_quantity} para intercambio</span>}
                    <span>{activeCard.condition || "Sin estado"}</span>
                  </div>
                  <div className="binder-feature-actions">
                    {cards.length > 0 && activeCard.id && !activeCard.is_missing && (
                      <Button variant="outline-dark" size="sm" onClick={() => openEditModal(activeCard)}>
                        <i className="bi bi-pencil-square me-2" />
                        Editar
                      </Button>
                    )}
                    {activeCard.id && !activeCard.is_missing && (
                      pendingDeleteCardId === activeCard.id ? (
                        <div className="inline-confirm compact">
                          <span>Se eliminara de {activeCollection?.name || "tu coleccion"} junto con sus fotos.</span>
                          <Button variant="danger" size="sm" onClick={() => deleteBinderCard(activeCard)}>Si</Button>
                          <Button variant="outline-dark" size="sm" onClick={() => setPendingDeleteCardId(null)}>No</Button>
                        </div>
                      ) : (
                        <Button variant="outline-danger" size="sm" onClick={() => setPendingDeleteCardId(activeCard.id)}>
                          <i className="bi bi-trash me-2" />
                          Borrar
                        </Button>
                      )
                    )}
                    {activeCard.is_missing && activeCard.catalog_card_id && activeCollectionId !== "all" && (
                      <Button variant="outline-dark" size="sm" onClick={() => openExistingAssignment(activeCard)}>
                        <i className="bi bi-folder-symlink me-2" />Vincular existente
                      </Button>
                    )}
                    {activeCard.is_missing && activeCard.catalog_card_id && activeCollectionId !== "all" && (
                      <BinderQuickActions
                        isWanted={activeCard.status === "wanted"}
                        loadingAction={binderActionLoading}
                        onHave={() => openQuickCatalogAdd(activeCard)}
                        onPhoto={() => openMissingCardScanner(activeCard)}
                        onWant={activeCard.status === "wanted" ? undefined : () => addMissingCardToWanted(activeCard)}
                      />
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="binder-empty-state">
                <i className="bi bi-journal-album" />
                <h2>Tu binder esta vacio</h2>
                <p className="muted-copy">Crea un binder personalizado o sube tu primera carta real.</p>
                <div className="d-flex gap-2 justify-content-center flex-wrap">
                  <Button className="btn-cardora" onClick={openAddModal}>Agregar carta</Button>
                  <Button variant="outline-dark" onClick={openWantedCardModal}>Publicar carta buscada</Button>
                </div>
              </div>
            )}
          </section>
        </Col>

        <Col xl={8}>
          <section className="binder-carousel-panel">

            {binderViewMode === "list" ? (
              <div className="binder-list-view">
                {filteredCards.map((card, index) => (
                  <button type="button" className="binder-list-row" key={binderItemKey(card)} onClick={() => { setActiveIndex(index); setBinderPage(Math.floor(index / BINDER_CARDS_PER_PAGE)); setShowMobileCardModal(true); }}>
                    <BinderCardImage src={mediaUrl(cardFrontImage(card))} alt={card.name} />
                    <span className="binder-list-copy"><strong>{card.name}</strong><small>{[card.set_name, card.collector_number, card.condition].filter(Boolean).join(" - ")}</small></span>
                    <span className="binder-list-flags"><BinderPocketIndicators card={card} />{Number(card.quantity) > 1 && <b>x{card.quantity}</b>}</span>
                    <i className="bi bi-chevron-right" />
                  </button>
                ))}
                {!filteredCards.length && <div className="binder-empty-state compact"><i className="bi bi-search" /><h2>Sin cartas</h2><p>No hay cartas para este filtro.</p></div>}
              </div>
            ) : (
            <div className="binder-page-stage">
              <Button variant="light" className="binder-page-side is-previous" disabled={binderPage === 0} onClick={() => moveBinderPage(-1)} aria-label="Pagina anterior" title="Pagina anterior"><i className="bi bi-chevron-left" /></Button>
              <span className="binder-page-indicator">Hoja {binderPage + 1} de {binderPageCount}</span>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePocketDragEnd}>
              <SortableContext items={currentDraggableIds} strategy={rectSortingStrategy}>
                <div className="binder-pocket-grid">
                  {currentBinderPageCards.map((card, pocketIndex) => {
                    const pocketNumber = binderPage * BINDER_CARDS_PER_PAGE + pocketIndex;
                    const selectPocket = () => {
                      const cardKey = binderItemKey(card);
                      const nextIndex = filteredCards.findIndex(item => binderItemKey(item) === cardKey);
                      if (nextIndex >= 0) {
                        setActiveIndex(nextIndex);
                        setBinderPage(Math.floor(nextIndex / BINDER_CARDS_PER_PAGE));
                        setPreviewSide("front");
                        setIsPreviewZoomed(false);
                        setShowMobileCardModal(true);
                      }
                    };

                    if (!card.id || card.is_missing) {
                      return (
                        <button
                          className={`binder-pocket ${card.is_missing ? "is-missing" : ""} ${activeIndex === pocketNumber ? "is-active" : ""}`}
                          key={`pocket-${binderItemKey(card)}`}
                          type="button"
                          onClick={selectPocket}
                        >
                          <BinderCardImage src={mediaUrl(cardFrontImage(card))} alt={card.name} />
                          <small>{pocketNumber + 1}</small>
                          <BinderPocketIndicators card={card} />
                          <RarityMark rarity={card.rarity} options={catalogOptions.rarities} compact />
                        </button>
                      );
                    }

                    return (
                      <SortableBinderPocket
                        card={card}
                        index={pocketNumber}
                        isActive={activeIndex === pocketNumber}
                        isDisabled={!canReorderBinder}
                        rarityOptions={catalogOptions.rarities}
                        key={`pocket-${card.id}`}
                        onSelect={selectPocket}
                      />
                    );
                  })}
                  {Array.from({ length: emptyPocketCount }).map((_, index) => (
                    <div className="binder-pocket binder-pocket-empty" key={`empty-pocket-${index}`}>
                      <small>{binderPage * BINDER_CARDS_PER_PAGE + currentBinderPageCards.length + index + 1}</small>
                    </div>
                  ))}
                </div>
              </SortableContext>
              </DndContext>
              <Button variant="light" className="binder-page-side is-next" disabled={binderPage >= binderPageCount - 1} onClick={() => moveBinderPage(1)} aria-label="Pagina siguiente" title="Pagina siguiente"><i className="bi bi-chevron-right" /></Button>
            </div>
            )}
          </section>
        </Col>
      </Row>

      <Modal show={showShareModal} onHide={() => setShowShareModal(false)} centered contentClassName="binder-share-modal">
        <Modal.Header closeButton>
          <div><Modal.Title>Compartir binder</Modal.Title><small>{activeCollection?.name || "Todos mis binders"} - {currentUser?.display_name || currentUser?.name || "Mi coleccion"}</small></div>
        </Modal.Header>
        <Modal.Body>
          <p className="muted-copy">Elige que parte de tu coleccion quieres compartir.</p>
          <div className="binder-share-options">
            <button type="button" onClick={() => copyMyBinderLink("binder")}><i className="bi bi-journal-album" /><span><strong>Binder actual</strong><small>Comparte el binder que estas viendo.</small></span><i className="bi bi-share" /></button>
            <button type="button" onClick={() => copyMyBinderLink("trade")}><i className="bi bi-arrow-left-right" /><span><strong>Copias e intercambio</strong><small>Tus repetidas y cartas disponibles.</small></span><i className="bi bi-share" /></button>
            <button type="button" onClick={() => copyMyBinderLink("wanted")}><i className="bi bi-search-heart" /><span><strong>Cartas buscadas</strong><small>Muestra las cartas que estas buscando.</small></span><i className="bi bi-share" /></button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showQuickCatalogSearch} onHide={() => setShowQuickCatalogSearch(false)} centered size="lg" scrollable>
        <Modal.Header closeButton><Modal.Title>Agregar desde catalogo</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="muted-copy">Busca una carta, indica cuantas copias tienes y decide si alguna esta disponible para intercambio. No necesitas subir una foto.</p>
          <Form.Control autoFocus value={quickCatalogSearch} onChange={event => setQuickCatalogSearch(event.target.value)} placeholder="Nombre, set o numero de carta..." />
          {isSearchingQuickCatalog && <div className="text-center py-4"><Spinner animation="border" /></div>}
          {!isSearchingQuickCatalog && quickCatalogSearch.trim().length >= 2 && !quickCatalogResults.length && <Alert variant="info" className="mt-3 mb-0">No encontramos esa carta en los catalogos importados.</Alert>}
          <div className="binder-missing-grid mt-3">
            {quickCatalogResults.map(card => (
              <button type="button" className="binder-missing-item" key={card.id} onClick={() => { setShowQuickCatalogSearch(false); openQuickCatalogAdd({ ...card, catalog_card_id: card.id }, activeCollectionId); }}>
                <img src={mediaUrl(card.image_url)} alt={card.name} />
                <span><strong>{card.name}</strong><small>{[card.set_name, card.collector_number].filter(Boolean).join(" - ")}</small></span>
                <i className="bi bi-plus-circle" />
              </button>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={Boolean(quickCatalogCard)} onHide={() => !isSavingQuickCard && setQuickCatalogCard(null)} centered>
        <Modal.Header closeButton><Modal.Title>Agregar desde catalogo</Modal.Title></Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <div className="binder-quick-add-card">
            <img src={mediaUrl(quickCatalogCard?.image_url)} alt={quickCatalogCard?.name || "Carta"} />
            <div><strong>{quickCatalogCard?.name}</strong><small>{[quickCatalogCard?.set_name, quickCatalogCard?.collector_number].filter(Boolean).join(" · ")}</small><Badge bg="light" text="dark">Imagen de referencia</Badge></div>
          </div>
          <Alert variant="info" className="py-2 mt-3">La agregaremos sin pedirte una foto. Si cargas más de una copia, dejaremos una para tu colección y las demás quedarán disponibles para intercambio. Luego puedes subir fotos reales desde <strong>Editar carta</strong>.</Alert>
          <Row className="g-3">
            <Col xs={12} md={6}><Form.Label>Copias que tienes</Form.Label><div className="binder-quantity-stepper"><button type="button" onClick={() => { const quantity = Math.max(1, quickQuantity - 1); setQuickQuantity(quantity); setQuickTradeEnabled(quantity > 1); setQuickAvailableQuantity(quantity > 1 ? quantity - 1 : 0); }} aria-label="Quitar copia"><i className="bi bi-dash" /></button><strong>{quickQuantity}</strong><button type="button" onClick={() => { const quantity = Math.min(99, quickQuantity + 1); setQuickQuantity(quantity); setQuickTradeEnabled(quantity > 1); setQuickAvailableQuantity(quantity > 1 ? quantity - 1 : 0); }} aria-label="Agregar copia"><i className="bi bi-plus" /></button></div></Col>
            <Col xs={12} md={6}><Form.Label>Intercambio</Form.Label><Form.Check type="switch" id="quick-trade-toggle" checked={quickTradeEnabled} disabled={quickQuantity > 1} onChange={event => { setQuickTradeEnabled(event.target.checked); setQuickAvailableQuantity(event.target.checked ? Math.max(1, quickAvailableQuantity) : 0); }} label="Disponible para intercambio" />{quickTradeEnabled && <div className="binder-quantity-stepper compact mt-2"><button type="button" disabled={quickQuantity > 1 && quickAvailableQuantity <= quickQuantity - 1} onClick={() => setQuickAvailableQuantity(value => Math.max(quickQuantity > 1 ? quickQuantity - 1 : 1, value - 1))} aria-label="Quitar disponible"><i className="bi bi-dash" /></button><strong>{Math.min(quickQuantity, Math.max(quickQuantity - 1, quickAvailableQuantity))}</strong><button type="button" disabled={quickAvailableQuantity >= quickQuantity} onClick={() => setQuickAvailableQuantity(value => Math.min(quickQuantity, value + 1))} aria-label="Agregar disponible"><i className="bi bi-plus" /></button></div>}</Col>
            <Col xs={12} md={6}><Form.Label>Acabado de la copia</Form.Label><Form.Select value={quickCardFinish} onChange={event => setQuickCardFinish(event.target.value)}>{CARD_FINISH_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</Form.Select></Col>
            <Col xs={12}><Form.Label>Condicion fisica</Form.Label><Form.Select value={quickCondition} onChange={event => setQuickCondition(event.target.value)}><option>Near Mint</option><option>Excellent</option><option>Good</option><option>Played</option></Form.Select></Col>
          </Row>
        </Modal.Body>
        <Modal.Footer><Button variant="outline-dark" disabled={isSavingQuickCard} onClick={() => setQuickCatalogCard(null)}>Cancelar</Button><Button className="btn-cardora" disabled={isSavingQuickCard} onClick={saveQuickCatalogCard}>{isSavingQuickCard ? <Spinner size="sm" /> : "Agregar al binder"}</Button></Modal.Footer>
      </Modal>

      <Modal show={showThemeSuggestionsModal} onHide={() => !themeSuggestionActionId && !isSavingThemeSuggestionsBulk && setShowThemeSuggestionsModal(false)} centered size="lg" scrollable contentClassName="binder-missing-modal">
        <Modal.Header closeButton><Modal.Title>{binderThemeText(activeCollection)}</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="muted-copy">Construye este binder con cartas que ya cargaste o explora el catalogo para descubrir otras del mismo tema.</p>
          <div className="binder-theme-source-toggle" role="group" aria-label="Origen de las cartas">
            <Button size="sm" variant={themeSuggestionSource === "mine" ? "dark" : "outline-dark"} onClick={() => { setThemeSuggestionSource("mine"); setThemeSuggestionSetFilter(""); setSelectedThemeSuggestionIds([]); }}>
              <i className="bi bi-collection me-2" />Mis cartas ({ownedThemeSuggestions.length})
            </Button>
            <Button size="sm" variant={themeSuggestionSource === "catalog" ? "dark" : "outline-dark"} onClick={() => { setThemeSuggestionSource("catalog"); setThemeSuggestionSetFilter(""); setSelectedThemeSuggestionIds([]); }}>
              <i className="bi bi-stars me-2" />Explorar catalogo ({themeSuggestions.length})
            </Button>
          </div>
          {themeSuggestionSource === "mine" && <Alert variant="info" className="py-2 mt-3">Las cartas seleccionadas se moveran desde su binder actual. Conservaremos fotos, copias, estado e historial.</Alert>}
          {formError && <Alert variant="warning">{formError}</Alert>}
          {isLoadingThemeSuggestions ? (
            <div className="text-center py-5"><Spinner animation="border" /><p className="muted-copy mt-2">Buscando cartas compatibles...</p></div>
          ) : activeThemeSuggestions.length ? (
            <>
              <div className="binder-theme-suggestions-toolbar">
                <Form.Group>
                  <Form.Label>Set</Form.Label>
                  <Form.Select
                    value={themeSuggestionSetFilter}
                    disabled={isSavingThemeSuggestionsBulk}
                    onChange={event => {
                      setThemeSuggestionSetFilter(event.target.value);
                      setSelectedThemeSuggestionIds([]);
                    }}
                  >
                    <option value="">Todos los sets ({activeThemeSuggestions.length})</option>
                    {[...new Map(activeThemeSuggestions.map(card => [card.card_set_id, card])).values()]
                      .sort((left, right) => left.set_name.localeCompare(right.set_name, "es"))
                      .map(set => (
                        <option value={set.card_set_id} key={set.card_set_id}>
                          {set.set_name}{set.set_code ? ` · ${set.set_code}` : ""} ({activeThemeSuggestions.filter(card => card.card_set_id === set.card_set_id).length})
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
                <div className="binder-missing-select-actions">
                  <Button
                    size="sm"
                    variant="outline-dark"
                    disabled={!selectableThemeSuggestions.length || isSavingThemeSuggestionsBulk}
                    onClick={() => setSelectedThemeSuggestionIds(
                      selectedThemeSuggestionIds.length === selectableThemeSuggestions.length
                        ? []
                        : selectableThemeSuggestions.map(card => themeSuggestionSource === "mine" ? card.source_user_card_id : card.id)
                    )}
                  >
                    {selectedThemeSuggestionIds.length === selectableThemeSuggestions.length && selectableThemeSuggestions.length
                      ? "Quitar seleccion"
                      : "Seleccionar todas"}
                  </Button>
                  <small>{selectedThemeSuggestionIds.length} seleccionadas</small>
                </div>
              </div>
              <div className="binder-missing-grid">
              {visibleThemeSuggestions.map(card => {
                const isOwnedSource = themeSuggestionSource === "mine";
                const selectionId = isOwnedSource ? card.source_user_card_id : card.id;
                const alreadyAdded = !isOwnedSource && Boolean(card.user_card_id);
                const busy = themeSuggestionActionId === card.id;
                const selected = selectedThemeSuggestionIds.includes(selectionId);
                const canSelect = isOwnedSource || !alreadyAdded || card.user_card_status === "wanted";
                return (
                  <article className={`binder-missing-item binder-theme-suggestion-item ${alreadyAdded ? "is-wanted" : ""} ${selected ? "is-selected" : ""}`} key={selectionId}>
                    <img src={mediaUrl(card.image_url)} alt={card.name} />
                    <span><strong>{card.name}</strong><small>{card.set_name}{card.collector_number ? ` #${card.collector_number}` : ""}</small></span>
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {isOwnedSource ? (
                        <Badge bg="info" text="dark">En {card.source_collection_name || "cartas sin binder"}</Badge>
                      ) : alreadyAdded ? (
                        <Badge bg={card.user_card_status === "wanted" ? "warning" : "success"} text={card.user_card_status === "wanted" ? "dark" : undefined}>{card.user_card_status === "wanted" ? "La buscas" : "En este binder"}</Badge>
                      ) : (
                        <BinderQuickActions
                          loadingAction={busy ? "wanted" : ""}
                          onHave={() => openQuickCatalogAdd({ ...card, catalog_card_id: card.id }, activeCollection.id)}
                          onPhoto={() => openThemeSuggestionScanner(card)}
                          onWant={() => addThemeSuggestionToWanted(card)}
                        />
                      )}
                      {!alreadyAdded && card.owned_elsewhere && <small className="text-muted w-100">Ya tienes una copia en otro binder.</small>}
                    </div>
                    {canSelect && <Form.Check
                      className="binder-theme-suggestion-check"
                      type="checkbox"
                      label={isOwnedSource ? "Mover al binder" : "Agregar al binder"}
                      checked={selected}
                      disabled={isSavingThemeSuggestionsBulk}
                      onChange={() => toggleThemeSuggestion(selectionId)}
                    />}
                  </article>
                );
              })}
              </div>
            </>
          ) : (
            <div className="binder-empty-state compact"><i className="bi bi-search" /><h2>Sin coincidencias</h2><p>Prueba editando el tema o importa mas catalogos desde superadmin.</p></div>
          )}
        </Modal.Body>
        {activeThemeSuggestions.length > 0 && <Modal.Footer>
          <Button variant="outline-dark" disabled={isSavingThemeSuggestionsBulk} onClick={() => setShowThemeSuggestionsModal(false)}>Cerrar</Button>
          <Button className="btn-cardora" disabled={!selectedThemeSuggestionIds.length || isSavingThemeSuggestionsBulk} onClick={addThemeSuggestionsToOwned}>
            {isSavingThemeSuggestionsBulk ? <><Spinner size="sm" className="me-2" />Agregando...</> : <><i className="bi bi-check2-square me-2" />{themeSuggestionSource === "mine" ? "Mover seleccionadas" : "Agregar seleccionadas"} ({selectedThemeSuggestionIds.length})</>}
          </Button>
        </Modal.Footer>}
      </Modal>

      <Modal show={showBulkDuplicatesModal} onHide={() => !isSavingBulkDuplicates && setShowBulkDuplicatesModal(false)} centered size="lg" scrollable contentClassName="binder-missing-modal">
        <Modal.Header closeButton><Modal.Title>Agregar copias repetidas</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="py-2">Si el total supera una copia, se conservará una para colección y las restantes quedarán automáticamente disponibles para intercambio y matches.</Alert>
          <p className="muted-copy">Selecciona cartas que ya tienes en este binder. La cantidad que indiques se sumará a las copias actuales; puedes agregar hasta 99 copias por carta.</p>
          {formError && <Alert variant="danger">{formError}</Alert>}
          {bulkDuplicateCards.length > 0 ? (
            <>
              <div className="binder-missing-select-actions">
                <Button
                  size="sm"
                  variant="outline-dark"
                  disabled={isSavingBulkDuplicates}
                  onClick={() => setSelectedDuplicateCardIds(
                    selectedDuplicateCardIds.length === bulkDuplicateCards.length
                      ? []
                      : bulkDuplicateCards.map(card => card.id)
                  )}
                >
                  {selectedDuplicateCardIds.length === bulkDuplicateCards.length ? "Quitar seleccion" : "Seleccionar todas"}
                </Button>
                <small>{selectedDuplicateCardIds.length} cartas seleccionadas</small>
              </div>
              <div className="binder-duplicate-list">
                {ownedCardsInBinder.map(card => {
                  const currentQuantity = Number(card.quantity) || 1;
                  const maxAddable = Math.max(0, 99 - currentQuantity);
                  const selected = selectedDuplicateCardIds.includes(card.id);
                  const quantity = Math.min(maxAddable || 1, Number(bulkDuplicateQuantities[card.id]) || 1);
                  return (
                    <article className={`binder-duplicate-row ${selected ? "is-selected" : ""}`} key={card.id}>
                      <Form.Check
                        id={`duplicate-select-${card.id}`}
                        type="checkbox"
                        checked={selected}
                        disabled={!maxAddable || isSavingBulkDuplicates}
                        onChange={() => toggleDuplicateCard(card.id)}
                        aria-label={`Seleccionar ${card.name}`}
                      />
                      <img src={mediaUrl(cardFrontImage(card))} alt={card.name} />
                      <div className="binder-duplicate-copy">
                        <strong>{card.name}</strong>
                        <small>{[card.set_name, card.collector_number].filter(Boolean).join(" · ")}</small>
                        <small>Ahora tienes {currentQuantity} {currentQuantity === 1 ? "copia" : "copias"}</small>
                      </div>
                      <Form.Group className="binder-duplicate-quantity">
                        <Form.Label htmlFor={`duplicate-quantity-${card.id}`}>Sumar</Form.Label>
                        <div className="binder-duplicate-stepper">
                          <button
                            type="button"
                            disabled={!selected || isSavingBulkDuplicates || !maxAddable || quantity <= 1}
                            onClick={() => setBulkDuplicateQuantities(current => ({ ...current, [card.id]: quantity - 1 }))}
                            aria-label={`Restar una copia de ${card.name}`}
                          >
                            <i className="bi bi-chevron-down" />
                          </button>
                          <Form.Control
                            id={`duplicate-quantity-${card.id}`}
                            type="number"
                            min="1"
                            max={maxAddable || 1}
                            value={quantity}
                            disabled={!selected || isSavingBulkDuplicates || !maxAddable}
                            onChange={event => setBulkDuplicateQuantities(current => ({
                              ...current,
                              [card.id]: Math.max(1, Math.min(maxAddable, Number(event.target.value) || 1))
                            }))}
                          />
                          <button
                            type="button"
                            disabled={!selected || isSavingBulkDuplicates || !maxAddable || quantity >= maxAddable}
                            onClick={() => setBulkDuplicateQuantities(current => ({ ...current, [card.id]: quantity + 1 }))}
                            aria-label={`Sumar una copia de ${card.name}`}
                          >
                            <i className="bi bi-chevron-up" />
                          </button>
                        </div>
                      </Form.Group>
                    </article>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="binder-empty-state compact"><i className="bi bi-check2-circle" /><h2>Sin copias para sumar</h2><p>No hay cartas de este binder con espacio para agregar más copias.</p></div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-dark" disabled={isSavingBulkDuplicates} onClick={() => setShowBulkDuplicatesModal(false)}>Cancelar</Button>
          <Button className="btn-cardora" disabled={!selectedDuplicateCardIds.length || isSavingBulkDuplicates} onClick={addSelectedDuplicates}>
            {isSavingBulkDuplicates ? <><Spinner size="sm" className="me-2" />Agregando...</> : <><i className="bi bi-copy me-2" />Sumar {selectedDuplicateCopies} copias</>}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showMissingCardsModal} onHide={() => !isSavingWantedBulk && setShowMissingCardsModal(false)} centered size="lg" scrollable contentClassName="binder-missing-modal">
        <Modal.Header closeButton><Modal.Title>Cartas faltantes de {activeCollection?.name}</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="binder-missing-summary"><span><strong>{missingSetCards.length}</strong> faltantes</span><span><strong>{missingSetCards.length - markableMissingCards.length}</strong> ya buscadas</span></div>
          {formError && <Alert variant="danger">{formError}</Alert>}
          {selectableMissingCards.length>0&&<div className="binder-missing-select-actions"><Button size="sm" variant="outline-dark" disabled={isSavingWantedBulk} onClick={()=>setSelectedMissingCardIds(selectedMissingCardIds.length===selectableMissingCards.length?[]:selectableMissingCards.map(card=>card.catalog_card_id))}>{selectedMissingCardIds.length===selectableMissingCards.length?"Quitar selección":"Seleccionar todas"}</Button><small>{selectedMissingCardIds.length} seleccionadas</small></div>}
          <div className="binder-missing-grid">{missingSetCards.map(card=>{const alreadyWanted=card.status==="wanted";const selected=selectedMissingCardIds.includes(card.catalog_card_id);return <button type="button" key={binderItemKey(card)} disabled={isSavingWantedBulk} className={"binder-missing-item "+(selected?"is-selected ":"")+(alreadyWanted?"is-wanted":"")} onClick={()=>toggleMissingCard(card.catalog_card_id)}><span className="binder-missing-check"><i className={"bi "+(selected?"bi-check-circle-fill":alreadyWanted?"bi-search-heart-fill":"bi-circle")}/></span><img src={mediaUrl(cardFrontImage(card))} alt={card.name}/><span><strong>{card.name}</strong><small>#{card.collector_number||"?"} · {alreadyWanted?"Ya la buscas":selected?"Seleccionada":"Seleccionar carta"}</small></span></button>})}</div>
          {!missingSetCards.length&&<div className="binder-empty-state compact"><i className="bi bi-check2-circle"/><h2>Set completo</h2><p>No tienes cartas faltantes en este binder.</p></div>}
        </Modal.Body>
        <Modal.Footer><Button variant="outline-dark" disabled={isSavingWantedBulk} onClick={()=>setShowMissingCardsModal(false)}>Cerrar</Button><Button variant="outline-dark" disabled={!selectedMissingCardIds.length||isSavingWantedBulk} onClick={markMissingCardsWanted}><i className="bi bi-search-heart me-2"/>Marcar como buscadas</Button><Button className="btn-cardora" disabled={!selectedMissingCardIds.length||isSavingWantedBulk} onClick={markMissingCardsOwned}>{isSavingWantedBulk?<Spinner size="sm" className="me-2"/>:<i className="bi bi-check-lg me-2"/>}Marcar como las tengo</Button></Modal.Footer>
      </Modal>

      <Modal show={showMobileCardModal && Boolean(activeCard)} onHide={() => setShowMobileCardModal(false)} centered contentClassName="binder-mobile-card-modal">
        <Modal.Header closeButton>
          <Modal.Title>{activeCard?.name || "Carta"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeCard && (
            <>
              <div className="binder-mobile-card-navigation" aria-label="Navegar cartas del binder">
                <Button variant="outline-dark" className="binder-icon-button" onClick={() => moveCarousel(-1)} aria-label="Carta anterior"><i className="bi bi-chevron-left" /></Button>
                <span>{activeIndex + 1} de {filteredCards.length}</span>
                <Button variant="outline-dark" className="binder-icon-button" onClick={() => moveCarousel(1)} aria-label="Carta siguiente"><i className="bi bi-chevron-right" /></Button>
              </div>
              <BinderCardViewer
                name={activeCard.name}
                frontImage={frontImage}
                backImage={backImage}
                side={previewSide}
                onRotate={rotatePreview}
                isZoomed={isPreviewZoomed}
                zoomOrigin={previewZoomOrigin}
                onMoveZoom={movePreviewZoom}
                onResetZoom={() => {
                  setIsPreviewZoomed(false);
                  setPreviewZoomOrigin({ x: 50, y: 50 });
                }}
                onToggleZoom={togglePreviewZoom}
                className="binder-mobile-card-stage"
                isMissing={activeCardIsVisuallyMissing}
              />
              <div className="binder-mobile-card-meta">
                <Badge bg={activeCard.status === "for_trade" ? "warning" : "success"} text={activeCard.status === "for_trade" ? "dark" : undefined}>
                  {statusLabels[activeCard.status] || activeCard.status || "Catalogo"}
                </Badge>
                <span>{activeCard.set_name} - {activeCard.collector_number}</span>
                <RarityMark rarity={activeCard.rarity} options={catalogOptions.rarities} />
                {activeCard.card_finish && activeCard.card_finish !== "unspecified" && <span>{cardFinishLabel(activeCard.card_finish)}</span>}
                <span>{activeCard.card_type || "Sin tipo"}</span>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="binder-mobile-card-actions">
          {activeCard?.id && !activeCard?.is_missing && (
            <Button variant="outline-dark" size="sm" onClick={() => { setShowMobileCardModal(false); openEditModal(activeCard); }}>
              <i className="bi bi-pencil-square me-2" />Editar
            </Button>
          )}
          {activeCard?.id && !activeCard?.is_missing && (
            pendingDeleteCardId === activeCard.id ? (
              <span className="inline-confirm compact">
                <span>Borrar?</span>
                <Button variant="danger" size="sm" onClick={() => { setShowMobileCardModal(false); deleteBinderCard(activeCard); }}>Si</Button>
                <Button variant="outline-dark" size="sm" onClick={() => setPendingDeleteCardId(null)}>No</Button>
              </span>
            ) : (
              <Button variant="outline-danger" size="sm" onClick={() => setPendingDeleteCardId(activeCard.id)}>
                <i className="bi bi-trash me-2" />Borrar
              </Button>
            )
          )}
          {activeCard?.is_missing && activeCard?.catalog_card_id && activeCollectionId !== "all" && (
            <Button variant="outline-dark" size="sm" onClick={() => openExistingAssignment(activeCard)}>
              <i className="bi bi-folder-symlink me-2" />Vincular existente
            </Button>
          )}
          {activeCard?.is_missing && activeCard?.catalog_card_id && activeCollectionId !== "all" && (
            <BinderQuickActions
              isWanted={activeCard.status === "wanted"}
              loadingAction={binderActionLoading}
              onHave={() => openQuickCatalogAdd(activeCard)}
              onPhoto={() => openMissingCardScanner(activeCard)}
              onWant={activeCard.status === "wanted" ? undefined : () => { setShowMobileCardModal(false); addMissingCardToWanted(activeCard); }}
            />
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(liveScannerSide)}
        onHide={() => setLiveScannerSide(null)}
        centered
        size="lg"
        dialogClassName="live-card-scanner-dialog"
        contentClassName="live-card-scanner-modal"
      >
        <Modal.Body>
          {liveScannerSide && (
            <LiveCardScanner
              key={liveScannerSide}
              side={liveScannerSide}
              onCapture={useLiveCardCapture}
              onCancel={() => setLiveScannerSide(null)}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(adjustingPhotoSide)}
        onHide={() => setAdjustingPhotoSide(null)}
        centered
        size="lg"
        className="card-photo-adjust-layer"
        backdropClassName="card-photo-adjust-backdrop"
        contentClassName="card-photo-adjust-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Ajustar {adjustingPhotoSide === "back" ? "reverso" : "frente"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {adjustingPhotoSide && (
            <div className="card-photo-adjust-editor">
              <div className="card-photo-adjust-stage">
                <div className="card-capture-preview has-image is-editor">
                  {(adjustingPhotoSide === "back" ? selectedBackPhotoPreview : selectedPhotoPreview) && (
                    <img src={adjustingPhotoSide === "back" ? selectedBackPhotoPreview : selectedPhotoPreview} alt="Preview ajuste" />
                  )}
                  <div className="card-capture-frame" />
                </div>
              </div>
              <div className="card-photo-adjust-panel">
                <strong>Encuadre</strong>
                <div className="card-photo-rotate-actions">
                  <Button
                    size="sm"
                    variant="outline-dark"
                    onClick={() => updatePhotoAdjustment(adjustingPhotoSide, { rotation: photoAdjustments[adjustingPhotoSide].rotation - 90 })}
                  >
                    <i className="bi bi-arrow-counterclockwise me-2" />90°
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-dark"
                    onClick={() => updatePhotoAdjustment(adjustingPhotoSide, { rotation: 0 })}
                  >
                    Reset
                  </Button>
                </div>
                <Form.Label>Rotacion fina ({Math.round(photoAdjustments[adjustingPhotoSide].rotation)}°)</Form.Label>
                <Form.Range min="-180" max="180" step="1" value={photoAdjustments[adjustingPhotoSide].rotation} onChange={event => updatePhotoAdjustment(adjustingPhotoSide, { rotation: Number(event.target.value) })} />
                <Form.Label>Zoom</Form.Label>
                <Form.Range min="1" max="1.8" step="0.05" value={photoAdjustments[adjustingPhotoSide].zoom} onChange={event => updatePhotoAdjustment(adjustingPhotoSide, { zoom: Number(event.target.value) })} />
                <Form.Label>Mover horizontal</Form.Label>
                <Form.Range min="-1" max="1" step="0.05" value={photoAdjustments[adjustingPhotoSide].offsetX} onChange={event => updatePhotoAdjustment(adjustingPhotoSide, { offsetX: Number(event.target.value) })} />
                <Form.Label>Mover vertical</Form.Label>
                <Form.Range min="-1" max="1" step="0.05" value={photoAdjustments[adjustingPhotoSide].offsetY} onChange={event => updatePhotoAdjustment(adjustingPhotoSide, { offsetY: Number(event.target.value) })} />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-cardora" onClick={() => setAdjustingPhotoSide(null)}>
            Listo
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(pendingDeleteCollectionId)} onHide={() => setPendingDeleteCollectionId(null)} centered>
        <Modal.Header closeButton><Modal.Title>Borrar {activeCollection?.name || "binder"}</Modal.Title></Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <p>Este binder contiene <strong>{activeCollection?.cards_count || 0} cartas reales</strong>. Elige que hacer con ellas.</p>
          <div className="binder-delete-options">
            <Form.Check type="radio" name="binder-delete-action" id="binder-delete-keep" checked={collectionDeleteForm.cardAction === "keep"} onChange={() => setCollectionDeleteForm({ cardAction: "keep", targetCollectionId: "" })} label="Conservar las cartas sin binder" />
            <small>Seguiran en tu coleccion y podras asignarlas luego sin volver a subir fotos.</small>
            <Form.Check type="radio" name="binder-delete-action" id="binder-delete-move" checked={collectionDeleteForm.cardAction === "move"} onChange={() => setCollectionDeleteForm(current => ({ ...current, cardAction: "move" }))} label="Mover todas a otro binder" />
            {collectionDeleteForm.cardAction === "move" && <Form.Select value={collectionDeleteForm.targetCollectionId} onChange={event => setCollectionDeleteForm(current => ({ ...current, targetCollectionId: event.target.value }))}><option value="">Selecciona destino</option>{collections.filter(collection => collection.id !== activeCollection?.id).map(collection => <option key={collection.id} value={collection.id}>{collection.name}</option>)}</Form.Select>}
            <Form.Check type="radio" name="binder-delete-action" id="binder-delete-all" checked={collectionDeleteForm.cardAction === "delete"} onChange={() => setCollectionDeleteForm({ cardAction: "delete", targetCollectionId: "" })} label="Borrar tambien todas las cartas" />
            <small className="text-danger">Esta opcion elimina definitivamente cartas, fotos, likes y referencias asociadas.</small>
          </div>
        </Modal.Body>
        <Modal.Footer><Button variant="outline-dark" onClick={() => setPendingDeleteCollectionId(null)}>Cancelar</Button><Button variant="danger" disabled={collectionDeleteForm.cardAction === "move" && !collectionDeleteForm.targetCollectionId} onClick={deleteCollection}>{collectionDeleteForm.cardAction === "delete" ? "Borrar todo" : "Borrar binder"}</Button></Modal.Footer>
      </Modal>

      <Modal show={Boolean(missingAssignmentCard)} onHide={() => !isAssigningExisting && setMissingAssignmentCard(null)} centered>
        <Modal.Header closeButton><Modal.Title>Vincular carta sin binder</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="muted-copy">Buscamos entre tus cartas sin binder una copia de <strong>{missingAssignmentCard?.name}</strong> {missingAssignmentCard?.collector_number ? `#${missingAssignmentCard.collector_number}` : ""}. La carta se movera a este binder sin duplicarse y conservara sus fotos.</p>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Form.Control className="mb-3" value={assignmentSearch} onChange={event => setAssignmentSearch(event.target.value)} placeholder="Buscar entre mis cartas sin binder..." />
          {assignmentCandidates.length ? (
            <div className="binder-assignment-list">
              {assignmentCandidates.map(card => {
                const exact = isExactAssignmentCandidate(card);
                const confirming = pendingManualAssignmentId === card.id;
                return <div className="binder-assignment-item" key={card.id}>
                  <button type="button" className="binder-assignment-card" disabled={isAssigningExisting} onClick={() => exact ? assignExistingCard(card) : setPendingManualAssignmentId(card.id)}>
                    <img src={mediaUrl(cardFrontImage(card))} alt={card.name} />
                    <span><strong>{card.name}</strong><small>{[card.set_name, card.collector_number, card.condition].filter(Boolean).join(" - ")}</small>{exact && <Badge bg="success">Coincidencia</Badge>}</span>
                    <i className="bi bi-arrow-right-circle" />
                  </button>
                  {confirming && !exact && <div className="inline-confirm">
                    <span>Los datos antiguos no coinciden exactamente. Confirma que esta foto corresponde a {missingAssignmentCard?.name} #{missingAssignmentCard?.collector_number}.</span>
                    <Button size="sm" className="btn-cardora" disabled={isAssigningExisting} onClick={() => assignExistingCard(card, true)}>Confirmar vinculo</Button>
                    <Button size="sm" variant="outline-dark" onClick={() => setPendingManualAssignmentId(null)}>Cancelar</Button>
                  </div>}
                </div>;
              })}
            </div>
          ) : (
            <Alert variant="info" className="mb-0">No tienes cartas sin binder con ese filtro. Las cartas que ya pertenecen a otro binder deben moverse primero desde <strong>Editar carta</strong>.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="outline-dark" disabled={isAssigningExisting} onClick={() => setMissingAssignmentCard(null)}>Cerrar</Button></Modal.Footer>
      </Modal>

      <Modal show={showAddModal} onHide={closeAddModal} size="lg" centered contentClassName="add-card-modal">
        <Modal.Header closeButton>
          <Modal.Title>{selectedStatus === "wanted" ? "Agregar carta buscada" : "Subir carta real"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {savedCardName ? (
            <div className="scan-save-success">
              <span className="scan-save-success-icon"><i className="bi bi-check-lg" /></span>
              <Badge bg="success">Guardada</Badge>
              <h3>{savedCardName}</h3>
              <p>{isAddingSetCard
                ? "La carta ya esta en tu binder vinculada a su posicion del set."
                : "La carta ya esta en tu binder y sus datos ayudaran a mejorar futuras identificaciones."}</p>
              {batchQueue.length > 0 && <strong>{batchQueue.length} {batchQueue.length === 1 ? "carta pendiente" : "cartas pendientes"} en la cola</strong>}
              <div className="scan-save-success-actions">
                <Button variant="outline-dark" onClick={closeAddModal}>{isAddingSetCard ? "Volver al binder" : "Finalizar"}</Button>
                {!isAddingSetCard && (
                  <Button className="btn-cardora" onClick={continueScanning}>
                    <i className="bi bi-camera-fill me-2" />{batchQueue.length ? "Revisar siguiente" : "Escanear otra"}
                  </Button>
                )}
              </div>
              {isAddingSetCard && targetCatalogCardId && (
                <div className="mt-4 text-start">
                  {catalogReportSubmitted ? (
                    <Alert variant="success" className="mb-0">Gracias. El reporte quedo pendiente de revision.</Alert>
                  ) : (
                    <Form onSubmit={submitCatalogIssue} className="border rounded-3 p-3">
                      <strong>¿Hay algun dato incorrecto en esta carta?</strong>
                      <p className="small text-muted mb-2">Puedes reportarlo para que el equipo revise el catalogo. Tu foto ya esta vinculada a esta carta.</p>
                      {catalogReportError && <Alert variant="danger" className="py-2">{catalogReportError}</Alert>}
                      <Form.Select className="mb-2" value={catalogIssueType} onChange={event => setCatalogIssueType(event.target.value)} aria-label="Tipo de incidencia">
                        <option value="incorrect_name">Nombre incorrecto</option>
                        <option value="incorrect_set">Set incorrecto</option>
                        <option value="incorrect_number">Numero incorrecto</option>
                        <option value="incorrect_rarity">Rareza incorrecta</option>
                        <option value="incorrect_stage">Fase o tipo incorrecto</option>
                        <option value="incorrect_energy_type">Tipo elemental incorrecto</option>
                        <option value="photo_mismatch">La imagen del catalogo no coincide</option>
                        <option value="other">Otro problema</option>
                      </Form.Select>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        minLength={5}
                        maxLength={1000}
                        required
                        value={catalogIssueDetails}
                        onChange={event => setCatalogIssueDetails(event.target.value)}
                        placeholder="Cuéntanos qué dato habría que revisar"
                      />
                      <div className="d-flex justify-content-end mt-2">
                        <Button type="submit" variant="outline-dark" disabled={isSubmittingCatalogReport}>
                          {isSubmittingCatalogReport ? <Spinner size="sm" /> : "Enviar reporte"}
                        </Button>
                      </div>
                    </Form>
                  )}
                </div>
              )}
            </div>
          ) : (
          <>
          {formError && <Alert variant="danger">{formError}</Alert>}
          {selectedStatus === "wanted" && (
            <Alert variant="info" className="py-2">
              Publica la carta que buscas sin foto. Con nombre, set y numero ya podemos generar matches con otros binders.
            </Alert>
          )}

          {scannerEnabled && selectedStatus !== "wanted" && (
            <section className="scan-first-panel">
              <div className="scan-wizard-steps">
                {[
                  ["photo", "1", "Foto"],
                  ["details", "2", "Datos"],
                  ["destination", "3", "Destino"]
                ].map(([step, number, label]) => (
                  <button
                    type="button"
                    key={step}
                    className={`scan-wizard-step ${scanStep === step ? "is-active" : ""} ${["photo", "details", "destination"].indexOf(step) < ["photo", "details", "destination"].indexOf(scanStep) ? "is-done" : ""} ${["details", "destination"].includes(step) && !selectedPhoto ? "is-disabled" : ""}`}
                    onClick={() => {
                      if (step === "photo" || selectedPhoto) setScanStep(step);
                    }}
                  >
                    <span>{number}</span>
                    {label}
                  </button>
                ))}
              </div>
              <div className="scan-first-head">
                <div>
                  <Badge bg="dark">{scanStep === "photo" ? "Paso 1" : scanStep === "details" ? "Paso 2" : "Paso 3"}</Badge>
                  <h3>{scanStep === "photo" ? "Captura la carta" : scanStep === "details" ? "Revisa los datos" : "Elige destino"}</h3>
                  <p>{scanStep === "photo"
                    ? isAddingSetCard
                      ? "Esta posicion ya identifica la carta. Sube una foto de tu copia para revisar los datos precargados y guardarla."
                      : "Saca o sube una foto frontal. El marco ayuda a encuadrar; despues puedes ajustar y corregir datos."
                    : scanStep === "details"
                      ? "Edita nombre, set, numero, rareza e idioma antes de guardarla."
                      : "Define el binder, la visibilidad y si la carta queda en coleccion o disponible para intercambio."}</p>
                </div>

              </div>

              {scanStep === "photo" && (
              <div className="scan-first-grid">
                <div className="scan-upload-card">
                  {capturePreview && (
                    <div className="card-capture-preview has-image">
                      <img src={capturePreview} alt={isAddingSetCard ? "Foto recortada de tu copia" : "Carta a analizar"} />
                      <span className="scan-photo-ready"><i className="bi bi-check-circle-fill" />Foto lista</span>
                      {selectedPhotoOriginal && (
                        <Button
                          size="sm"
                          variant="light"
                          className="card-photo-adjust-overlay scan-preview-adjust"
                          onClick={() => setAdjustingPhotoSide("front")}
                        >
                          <i className="bi bi-crop me-2" />Ajustar recorte
                        </Button>
                      )}
                    </div>
                  )}
                  {capturePreview && (
                    <div className="scan-next-action">
                      <strong>{isAddingSetCard ? "Foto lista para tu carta del set" : "Recorte guardado"}</strong>
                      <span>{isAddingSetCard ? "La informacion de esta posicion ya esta cargada." : "Ahora continua con el analisis para completar los datos."}</span>
                    </div>
                  )}
                  <Button variant={capturePreview ? "outline-dark" : undefined} className={capturePreview ? "" : "btn-cardora"} onClick={() => setLiveScannerSide("front")}>
                    <i className="bi bi-bounding-box-circles me-2" />{capturePreview ? "Repetir foto" : "Escanear en vivo"}
                  </Button>
                  <div className="scan-upload-divider"><span>o subir una foto</span></div>
                  <Form.Control
                    className="mt-2"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple={!isAddingSetCard}
                    onChange={event => {
                      const files = Array.from(event.target.files || []).slice(0, 9);
                      const [file, ...queued] = files;
                      setBatchQueue(queued);
                      setScanFile(file || null);
                      setScanResult(null);
                      setScanAttemptCount(0);
                      setSelectedScanCardId("");
                      if (isAddingSetCard) setSelectedPhoto(null);
                      processPhoto(file || null, setSelectedPhoto, "front");
                    }}
                  />
                  {batchQueue.length > 0 && (
                    <div className="scan-batch-queue">
                      <strong><i className="bi bi-collection me-2" />Cola: {batchQueue.length + 1}/9</strong>
                      <span>Se analizara una carta por vez para que puedas revisar cada resultado.</span>
                    </div>
                  )}
                </div>

                <div className="scan-card-results">
                  {isAddingSetCard ? (
                    <>
                      <Alert variant="success" className="py-2">
                        <strong>{customCardForm.name || "Carta del set"}</strong>
                        <span className="d-block">
                          {[customCardForm.setName, customCardForm.collectorNumber, customCardForm.rarity].filter(Boolean).join(" · ")}
                        </span>
                        <small>Datos precargados desde la posicion seleccionada.</small>
                      </Alert>
                      <div className="scan-card-actions scan-photo-footer">
                        <Button
                          className="btn-cardora"
                          disabled={!scanFile || !selectedPhoto || isProcessingPhoto}
                          onClick={continueWithSetCard}
                        >
                          {isProcessingPhoto ? "Preparando foto..." : "Usar foto y revisar datos"}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <Form.Label>Idioma de la carta</Form.Label>
                      <Form.Select
                        className="mb-3"
                        value={customCardForm.language}
                        onChange={event => setCustomCardForm(current => ({ ...current, language: event.target.value }))}
                      >
                        <option value="es">Español</option>
                        <option value="en">Inglés</option>
                        <option value="jp">Japonés</option>
                        <option value="other">Otro / no estoy seguro</option>
                      </Form.Select>
                      <Form.Text className="d-block mb-3">Se usa como ayuda de lectura; no determina el set automaticamente.</Form.Text>
                      <Form.Label>Texto opcional</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={scanText}
                        onChange={event => setScanText(event.target.value)}
                        placeholder={"Si quieres ayudar: Charizard ex\n223/197"}
                      />
                      <div className="scan-card-actions scan-photo-footer">
                        <Button className="btn-cardora" disabled={isScanningCard || (!scanFile && !scanText.trim()) || scanLimitReached} onClick={runCardScan}>
                          {isScanningCard ? "Analizando..." : scanAttemptCount === 1 && scanWouldUseAi ? "Reanalizar una vez" : scanFile ? "Continuar: analizar carta" : "Analizar / completar"}
                        </Button>
                        {scanFile && (
                          <Button variant="outline-dark" onClick={useScanAsManualCard}>
                            Usar foto y cargar manual
                          </Button>
                        )}
                      </div>
                    </>
                  )}

                  {scanNotice && (
                    <Alert variant="info" className="py-2">{scanNotice}</Alert>
                  )}
                  {scanWouldUseAi && (
                    <small className="muted-copy d-block mb-2">
                      IA: {Math.min(scanAttemptCount, 2)}/2 intentos usados para esta foto.
                    </small>
                  )}
                  {scanResult?.ai?.used && (
                    <Alert variant="success" className="py-2">
                      IA detecto: {scanResult.ai.recognition?.name || "carta"} {scanResult.ai.recognition?.collectorNumber ? `· ${scanResult.ai.recognition.collectorNumber}` : ""}{scanResult.ai.recognition?.rarity ? ` · ${scanResult.ai.recognition.rarity}` : ""}
                    </Alert>
                  )}
                  {!scanResult?.ai?.used && scanResult?.ai?.reason && scanResult.ai.reason !== "manual_text_present_or_no_image" && (
                    <Alert variant="light" className="py-2">
                      {scanResult.ai.reason === "reanalysis_limit_reached" ? "Limite de reanalisis alcanzado. Puedes completar datos manualmente." : `IA no usada: ${scanResult.ai.reason}. Puedes completar datos manualmente.`}
                    </Alert>
                  )}
                </div>
              </div>
              )}

              {scanStep === "details" && !isAddingSetCard && (
                <div className="scan-detected-summary">
                  <div className="scan-review-photos">
                    <div className={`card-capture-preview ${scanPreview ? "has-image" : ""}`}>
                      {scanPreview ? <img src={scanPreview} alt="Frente analizado" /> : <span>Frente</span>}
                    </div>
                    {selectedBackPhotoPreview && (
                      <div className="card-capture-preview has-image">
                        <img src={selectedBackPhotoPreview} alt="Reverso" />
                      </div>
                    )}
                  </div>
                  <div>
                    <strong>{customCardForm.name || "Carta sin nombre"}</strong>
                    <span>{customCardForm.setName || "Set pendiente"} {customCardForm.collectorNumber ? `· ${customCardForm.collectorNumber}` : ""}</span>
                    <div className="scan-detected-tags">
                      <Badge bg={customCardForm.rarity ? "warning" : "light"} text="dark">
                        {customCardForm.rarity || "Rareza pendiente"}
                      </Badge>
                      <Badge bg="light" text="dark">{customCardForm.language || "idioma"}</Badge>
                    </div>
                    {scanNotice && <Alert variant="info" className="py-2 mt-2">{scanNotice}</Alert>}
                    {scanStep === "details" && scanResult?.matches?.length > 0 && (
                      <div className="scan-match-options">
                        <div><strong>Posibles coincidencias</strong><small className="d-block">Selecciona una solo si corresponde a tu carta.</small></div>
                        {scanResult.matches.slice(0, 4).map(match => (
                          <button
                            type="button"
                            key={match.id}
                            className={selectedScanCardId === match.id ? "is-selected" : ""}
                            onClick={() => {
                              setSelectedScanCardId(match.id);
                              setCustomCardForm(current => ({
                                ...current,
                                name: match.name || current.name,
                                setName: match.set_name || current.setName,
                                collectorNumber: match.collector_number || current.collectorNumber,
                                rarity: match.rarity || current.rarity
                              }));
                              setScanNotice("Carta del catalogo seleccionada. Comprueba los datos y continua.");
                            }}
                          >
                            <span><strong>{match.name}</strong><small>{match.set_name || "Set pendiente"}{match.collector_number ? ` - ${match.collector_number}` : ""}</small></span>
                            <Badge bg={selectedScanCardId === match.id ? "success" : "light"} text={selectedScanCardId === match.id ? undefined : "dark"}>
                              {selectedScanCardId === match.id ? "Seleccionada" : match.confidence ? `Seleccionar · ${match.confidence}%` : "Seleccionar"}
                            </Badge>
                          </button>
                        ))}
                      </div>
                    )}

                  </div>
                </div>
              )}
            </section>
          )}

          <section className={scannerEnabled ? "manual-card-panel" : ""}>
            {scannerEnabled && addCardMode !== "scan" && scanStep !== "photo" && (
              <div className="manual-card-head">
                <Badge bg="light" text="dark">{scanStep === "details" ? "Paso 2" : "Paso 3"}</Badge>
                <h3>{scanStep === "details" ? "Datos editables" : "Destino de la carta"}</h3>
              </div>
            )}

            <Row className="g-3 mb-3">
              {selectedStatus === "wanted" && addCardMode === "custom" && (!scannerEnabled || scanStep === "details") && (
                <Col xs={12}>
                  <div className="wanted-card-helper">
                    <div className="flex-grow-1">
                      <Form.Label>Busca por nombre, set o numero</Form.Label>
                      <Form.Control
                        value={wantedText}
                        onChange={event => setWantedText(event.target.value)}
                        placeholder="Ej. Cinccino ASC 217"
                      />
                      <small className="muted-copy d-block mt-2">
                        Buscamos primero copias reales de la comunidad y luego referencias de catalogos publicados.
                      </small>
                    </div>
                    <Button variant="outline-dark" disabled={isIdentifyingWanted || !wantedText.trim()} onClick={identifyWantedText}>
                      <i className="bi bi-magic me-2" />{isIdentifyingWanted ? "Sugiriendo..." : "Completar datos"}
                    </Button>
                  </div>
                  {isSearchingWanted && <div className="muted-copy mt-2">Buscando en binders reales...</div>}
                  {!isSearchingWanted && wantedText.trim().length >= 2 && wantedRealMatches.length === 0 && (
                    <Alert variant="light" className="mt-2 mb-0">
                      Todavia nadie publico una copia real. Puedes elegir una referencia del catalogo y te avisaremos cuando alguien la tenga.
                    </Alert>
                  )}
                  {wantedRealMatches.length > 0 && (
                    <div className="wanted-real-results mt-2">
                      {wantedRealMatches.map(match => {
                        const realPhoto = match.photos?.find(photo => ["front", "binder"].includes(photo.photo_type)) || match.photos?.[0];
                        return (
                          <button
                            type="button"
                            className="wanted-real-result"
                            key={match.id}
                            onClick={() => {
                              setCustomCardForm(current => ({
                                ...current,
                                name: match.name || current.name,
                                setName: match.set_name || current.setName,
                                collectorNumber: match.collector_number || current.collectorNumber,
                                rarity: match.rarity || current.rarity
                              }));
                              setScanNotice("Coincidencia real encontrada. Revisa los datos y guardala como buscada.");
                            }}
                          >
                            {realPhoto?.photo_url ? <img src={mediaUrl(realPhoto.photo_url)} alt="" /> : <span className="wanted-real-placeholder"><i className="bi bi-card-image" /></span>}
                            <span>
                              <strong>{match.name}</strong>
                              <small>{match.set_name || "Set sin indicar"}{match.collector_number ? ` - ${match.collector_number}` : ""}</small>
                              <small>Carta real de {match.display_name}</small>
                            </span>
                            <i className="bi bi-plus-circle" />
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {wantedCatalogMatches.length > 0 && (
                    <div className="wanted-catalog-results mt-3">
                      <strong className="d-block mb-2">Referencias del catalogo</strong>
                      <div className="catalog-card-strip">
                        {wantedCatalogMatches.map(match => (
                          <button type="button" className={targetCatalogCardId === match.id ? "catalog-card-mini is-selected" : "catalog-card-mini"} key={match.id} onClick={() => {
                            setTargetCatalogCardId(match.id);
                            setCustomCardForm(current => ({ ...current, name: match.name || current.name, setName: match.set_name || current.setName, collectorNumber: match.collector_number || current.collectorNumber, rarity: match.rarity || current.rarity, language: match.metadata?.language || current.language }));
                            setScanNotice("Referencia de catalogo seleccionada. La alerta quedara vinculada a esta carta.");
                          }}>
                            {match.image_url ? <img src={match.image_url} alt={match.name} /> : <span>{match.collector_number || "?"}</span>}
                            <small>{match.name} · {match.set_code || match.set_name}</small>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </Col>
              )}
              {addCardMode === "custom" && (!scannerEnabled || scanStep === "details") && (
                <>
                  <Col md={6}>
                    <Form.Label>Nombre de la carta</Form.Label>
                    <Form.Control
                      value={customCardForm.name}
                      onChange={event => setCustomCardForm({ ...customCardForm, name: event.target.value })}
                      placeholder="Ej. Charizard EX"
                      required
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Set / coleccion</Form.Label>
                    <Form.Control
                      list="cardora-set-options"
                      value={customCardForm.setName}
                      onChange={event => setCustomCardForm({ ...customCardForm, setName: event.target.value })}
                      placeholder="Busca por nombre o codigo"
                      autoComplete="off"
                    />
                    <datalist id="cardora-set-options">
                      {catalogOptions.sets.map(set => (
                        <option key={set.id} value={set.name}>{set.code ? `${set.code} - ${set.name}` : set.name}</option>
                      ))}
                    </datalist>
                    {isUnknownSet && (
                      <Form.Text className="text-warning"><i className="bi bi-lightbulb me-1" />Set no catalogado: al guardar enviaremos una sugerencia al Data Center. No aparecera como binder prearmado hasta que se revise y publique.</Form.Text>
                    )}
                  </Col>
                  <Col md={6}>
                    <Form.Label>Numero</Form.Label>
                    <Form.Control
                      value={customCardForm.collectorNumber}
                      onChange={event => setCustomCardForm({ ...customCardForm, collectorNumber: event.target.value })}
                      placeholder="Ej. 199/165"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Rareza</Form.Label>
                    <Form.Select
                      value={customRarityMode ? "__other__" : customCardForm.rarity}
                      onChange={event => {
                        if (event.target.value === "__other__") {
                          setCustomRarityMode(true);
                          setCustomCardForm({ ...customCardForm, rarity: "" });
                          return;
                        }
                        setCustomRarityMode(false);
                        setCustomCardForm({ ...customCardForm, rarity: event.target.value });
                      }}
                    >
                      <option value="">Selecciona una rareza</option>
                      {customCardForm.rarity && !catalogOptions.rarities.some(rarity => rarity.display_name === customCardForm.rarity) && (
                        <option value={customCardForm.rarity}>Propuesta IA: {customCardForm.rarity}</option>
                      )}
                      {catalogOptions.rarities.map(rarity => (
                        <option key={rarity.id || rarity.code} value={rarity.display_name}>
                          {rarityGlyph(rarity)} {rarity.display_name}
                        </option>
                      ))}
                      <option value="__other__">No esta en la lista</option>
                    </Form.Select>
                    {customRarityMode && (
                      <Form.Control
                        className="mt-2"
                        value={customCardForm.rarity}
                        onChange={event => setCustomCardForm({ ...customCardForm, rarity: event.target.value })}
                        placeholder="Escribe la rareza que ves en la carta"
                        autoFocus
                      />
                    )}
                    {customCardForm.rarity && !catalogOptions.rarities.some(rarity => rarity.display_name === customCardForm.rarity) && (
                      <Form.Text>Rareza no catalogada: quedara pendiente de aprobacion.</Form.Text>
                    )}
                  </Col>
                  <Col md={6}>
                    <Form.Label>Tipo de carta / fase</Form.Label>
                    <Form.Control
                      list="cardora-card-type-options"
                      value={customCardForm.cardType}
                      onChange={event => setCustomCardForm({ ...customCardForm, cardType: event.target.value })}
                      placeholder="Ej. Pokemon Basico, Pokemon Fase 1, Energia"
                    />
                    <datalist id="cardora-card-type-options">
                      <option value="Pokemon Basico" />
                      <option value="Pokemon Fase 1" />
                      <option value="Pokemon Fase 2" />
                      <option value="Entrenador - Objeto" />
                      <option value="Entrenador - Partidario" />
                      <option value="Entrenador - Estadio" />
                      <option value="Energia" />
                    </datalist>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Tipo elemental</Form.Label>
                    <Form.Select
                      value={customCardForm.energyType}
                      onChange={event => setCustomCardForm({ ...customCardForm, energyType: event.target.value })}
                    >
                      <option value="">No identificado</option>
                      {ENERGY_TYPE_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
                    </Form.Select>
                    <Form.Text>
                      {isAddingSetCard && !customCardForm.energyType
                        ? "El catálogo todavía no aporta el tipo elemental de esta carta; puedes seleccionarlo manualmente."
                        : "Energia o afinidad visible de la carta; no es la fase del Pokemon."}
                    </Form.Text>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Acabado de esta copia</Form.Label>
                    <Form.Select value={customCardForm.cardFinish} onChange={event => setCustomCardForm({ ...customCardForm, cardFinish: event.target.value })}>
                      {CARD_FINISH_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </Form.Select>
                    <Form.Text>Indica si tu copia es normal, holo o reverse. Es independiente de la rareza.</Form.Text>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Idioma</Form.Label>
                    <Form.Select
                      value={customCardForm.language}
                      onChange={event => setCustomCardForm({ ...customCardForm, language: event.target.value })}
                    >
                      <option value="es">Español</option>
                      <option value="en">Inglés</option>
                      <option value="jp">Japonés</option>
                      <option value="other">Otro</option>
                    </Form.Select>
                  </Col>
                </>
              )}

              {selectedStatus !== "wanted" && addCardMode === "custom" && (!scannerEnabled || scanStep === "details") && !scanResult && !isAddingSetCard && (
                <>
                  <Col md={6}>
                    <Form.Label>Foto frontal</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={event => processPhoto(event.target.files?.[0], setSelectedPhoto, "front")}
                    />
                    {photoOptimizationInfo.front && <Form.Text>{photoOptimizationInfo.front}</Form.Text>}
                  </Col>
                  <Col md={6}>
                    <Form.Label>Foto reverso</Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={event => processPhoto(event.target.files?.[0], setSelectedBackPhoto, "back")}
                    />
                    {photoOptimizationInfo.back && <Form.Text>{photoOptimizationInfo.back}</Form.Text>}
                  </Col>
                  <Col xs={12}>
                    <div className="card-capture-guides">
                      <div className={`card-capture-preview ${selectedPhotoPreview ? "has-image" : ""}`}>
                        {selectedPhotoPreview ? <img src={selectedPhotoPreview} alt="Preview frente" /> : <span>Frente</span>}
                        <div className="card-capture-frame" />
                        {selectedPhotoOriginal && (
                          <Button size="sm" variant="light" className="card-photo-adjust-overlay" onClick={() => setAdjustingPhotoSide("front")}>
                            <i className="bi bi-sliders me-2" />Ajustar frente
                          </Button>
                        )}
                      </div>
                      <div className={`card-capture-preview ${selectedBackPhotoPreview ? "has-image" : ""}`}>
                        {selectedBackPhotoPreview ? <img src={selectedBackPhotoPreview} alt="Preview reverso" /> : <span>Reverso</span>}
                        <div className="card-capture-frame" />
                        {selectedBackPhotoOriginal && (
                          <Button size="sm" variant="light" className="card-photo-adjust-overlay" onClick={() => setAdjustingPhotoSide("back")}>
                            <i className="bi bi-sliders me-2" />Ajustar reverso
                          </Button>
                        )}
                      </div>
                    </div>
                    <small className="muted-copy d-block mt-2">
                      {isProcessingPhoto ? "Ajustando imagen..." : "Alinea la carta dentro del marco. En movil usa Ajustar si queda torcida o lejos."}
                    </small>
                  </Col>
                </>
              )}

              {addCardMode !== "scan" && (!scannerEnabled || scanStep === "destination") && (
                <>
                  <Col md={5}>
                    <Form.Label>Que queres hacer con esta copia</Form.Label>
                    <Form.Select value={selectedCardIntention} onChange={event => {
                      const intention = event.target.value;
                      setSelectedCardIntention(intention);
                      setSelectedStatus(intention === "for_trade" ? "for_trade" : "showcase");
                    }}>
                      <option value="collection">Guardar en mi coleccion</option>
                      <option value="open_to_trade">Abierto a escuchar interes</option>
                      <option value="for_trade">Disponible para intercambio</option>
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label>{isAddingSetCard ? "Binder del set" : "Binder"}</Form.Label>
                    {isAddingSetCard ? (
                      <>
                        <div className="form-control bg-light">
                          {collections.find(collection => collection.id === targetSetCollectionId)?.name || "Binder del set"}
                        </div>
                        <Form.Text>La carta se guardara en esta posicion del set.</Form.Text>
                      </>
                    ) : (
                      <Form.Select value={selectedCollectionId} onChange={event => {
                        const collectionId = event.target.value;
                        const binder = collections.find(collection => collection.id === collectionId);
                        const intention = binder?.default_card_intention || "collection";
                        setSelectedCollectionId(collectionId);
                        setSelectedCardIntention(intention);
                        setSelectedStatus(intention === "for_trade" ? "for_trade" : "showcase");
                      }}>
                        <option value="">Sin binder</option>
                        {collections.map(collection => (
                          <option value={collection.id} key={collection.id}>{collection.name}</option>
                        ))}
                      </Form.Select>
                    )}
                  </Col>
                  <Col md={3}>
                    <Form.Label>Condicion</Form.Label>
                    <Form.Select value={selectedCondition} onChange={event => setSelectedCondition(event.target.value)}>
                      <option>Near Mint</option>
                      <option>Excellent</option>
                      <option>Good</option>
                      <option>Played</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Label>Visibilidad</Form.Label>
                    <Form.Select value={selectedVisibility} onChange={event => setSelectedVisibility(event.target.value)}>
                      <option value="public">Publica</option>
                      <option value="followers">Seguidores</option>
                      <option value="private">Privada</option>
                    </Form.Select>
                  </Col>
                  <Col xs={12}>
                    <Form.Label>{selectedStatus === "wanted" ? "Detalles de busqueda" : "Notas de conexion"}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={selectedTradeNotes}
                      onChange={event => setSelectedTradeNotes(event.target.value)}
                      placeholder={selectedStatus === "wanted" ? "Idioma, version, prioridad o comentarios..." : "Estado real, idioma, que te interesaria a cambio..."}
                    />
                  </Col>
                  {selectedStatus !== "wanted" && (
                    <Col md={4}>
                      <Form.Label>Valor estimado EUR</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={selectedEstimatedValue}
                        onChange={event => setSelectedEstimatedValue(event.target.value)}
                        placeholder="Ej. 25.00"
                      />
                    </Col>
                  )}
                </>
              )}
            </Row>

            {addCardMode === "custom" && scannerEnabled && scanStep === "details" && (
              <div className="scan-card-actions scan-wizard-footer justify-content-end">
                <Button variant="outline-dark" onClick={() => setScanStep("photo")}>Volver a foto</Button>
                <Button className="btn-cardora" disabled={!customCardForm.name.trim() || (selectedStatus !== "wanted" && !selectedPhoto)} onClick={() => setScanStep("destination")}>
                  Continuar a destino
                </Button>
              </div>
            )}

            {addCardMode === "custom" && (!scannerEnabled || scanStep === "destination") && (
              <div className="scan-card-actions scan-wizard-footer justify-content-end">
                {scannerEnabled && <Button variant="outline-dark" onClick={() => setScanStep("details")}>Volver a datos</Button>}
                <Button className="btn-cardora" disabled={isSavingCard || isProcessingPhoto} onClick={() => addCardToBinder(null)}>
                  {isProcessingPhoto ? <><Spinner size="sm" className="me-2" />Preparando foto...</> : isSavingCard ? <><Spinner size="sm" className="me-2" />Guardando...</> : "Guardar carta"}
                </Button>
              </div>
            )}

          </section>
          </>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showCollectionModal}
        onHide={() => {
          if (isSavingCollection) return;
          setShowCollectionModal(false);
          setEditingCollectionId(null);
          setCollectionStep(1);
        }}
        centered
        size="lg"
        scrollable
        contentClassName="binder-wizard-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{editingCollectionId ? "Editar binder" : "Crear binder"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={event => {
          if (collectionStep < 3) {
            event.preventDefault();
            if (isSavingCollection || (collectionStep === 1 && !collectionForm.binderStructure)) return;
            setCollectionStep(step => Math.min(3, step + 1));
            return;
          }
          if (isSavingCollection) {
            event.preventDefault();
            return;
          }
          createCollection(event);
        }}>
          <Modal.Body>
            <div className="binder-wizard-progress" aria-label={`Paso ${collectionStep} de 3`}>
              {[1, 2, 3].map(step => (
                <div className={step <= collectionStep ? "is-active" : ""} key={step}>
                  <span>{step}</span>
                  {step < 3 && <i />}
                </div>
              ))}
            </div>
            {formError && <Alert variant="danger">{formError}</Alert>}

            {collectionStep === 1 && (
              <div className="binder-wizard-panel">
                <div>
                  <Form.Label>Como queres organizar tu binder</Form.Label>
                  <div className="binder-choice-grid binder-kind-grid">
                    <button type="button" className={collectionForm.binderStructure === "set" ? "is-selected" : ""} onClick={() => setCollectionForm(current => ({ ...current, binderStructure: "set", themeType: "", themeQuery: "" }))}>
                      <i className="bi bi-layers" />
                      <strong>Por set</strong>
                      <span>Prepara el progreso y las cartas faltantes de una expansion.</span>
                    </button>
                    <button type="button" className={collectionForm.binderStructure === "custom" && collectionForm.themeType ? "is-selected" : ""} onClick={() => setCollectionForm(current => ({
                      ...current,
                      binderStructure: "custom",
                      cardSetId: "",
                      themeType: current.themeType || "character",
                      themeQuery: current.themeType ? current.themeQuery : ""
                    }))}>
                      <i className="bi bi-stars" />
                      <strong>Por temática</strong>
                      <span>Pokémon, artista, rareza o tipo de carta.</span>
                    </button>
                    <button type="button" className={collectionForm.binderStructure === "custom" && !collectionForm.themeType ? "is-selected" : ""} onClick={() => setCollectionForm(current => ({
                      ...current,
                      binderStructure: "custom",
                      cardSetId: "",
                      themeType: "",
                      themeQuery: ""
                    }))}>
                      <i className="bi bi-grid-3x3-gap" />
                      <strong>Personalizado</strong>
                      <span>Organiza libremente tus cartas.</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {collectionStep === 2 && (
              <div className="binder-wizard-panel">
                {collectionForm.binderStructure === "custom" && (
                  <Form.Group>
                    <Form.Label>Nombre del binder</Form.Label>
                    <Form.Control
                      value={collectionForm.name}
                      onChange={event => setCollectionForm({ ...collectionForm, name: event.target.value })}
                      placeholder="Duplicadas, favoritas, Pikachu..."
                      required
                    />
                    <Form.Text>Los binders por set usan automaticamente el nombre oficial del catalogo.</Form.Text>
                  </Form.Group>
                )}
                {collectionForm.binderStructure === "custom" && collectionForm.themeType && (
                  <div className="border rounded p-3">
                    <Form.Text>Configura el tema de este binder.</Form.Text>
                    <Row className="g-2 mt-2">
                      <Col md={5}>
                        <Form.Label>Tipo de tema</Form.Label>
                        <Form.Select value={collectionForm.themeType} onChange={event => setCollectionForm({ ...collectionForm, themeType: event.target.value })}>
                          <option value="character">Personaje</option>
                          <option value="artist">Artista</option>
                          <option value="rarity">Rareza</option>
                          <option value="card_type">Tipo de carta</option>
                          <option value="energy_type">Tipo elemental</option>
                          <option value="custom">Otro</option>
                        </Form.Select>
                      </Col>
                      <Col md={7}>
                        <Form.Label>Que coleccionas</Form.Label>
                        <Form.Control
                          value={collectionForm.themeQuery}
                          maxLength={120}
                          list={collectionForm.themeType === "character" ? "binder-character-options" : undefined}
                          autoComplete="off"
                          onChange={event => setCollectionForm({ ...collectionForm, themeQuery: event.target.value })}
                          placeholder={collectionForm.themeType === "character" ? "Escribe para buscar, por ejemplo Pikachu" : "Describe el tema"}
                          required
                        />
                        {collectionForm.themeType === "character" && <datalist id="binder-character-options">
                          {characterThemeOptions.map(option => <option value={option.name} key={option.name}>{option.cards_count} cartas en {option.sets_count} sets</option>)}
                        </datalist>}
                        {collectionForm.themeType === "character" && <Form.Text>
                          {isLoadingCharacterThemes ? "Buscando nombres en los catalogos..." : characterThemeOptions.length ? `${characterThemeOptions.length} coincidencias disponibles. Tambien puedes escribir un nombre libre.` : "Escribe un nombre; tambien puedes usar uno que aun no este catalogado."}
                        </Form.Text>}
                      </Col>
                    </Row>
                  </div>
                )}
                {collectionForm.binderStructure === "set" && (
                  <div className="binder-set-picker">
                    <div className="binder-set-picker-toolbar">
                      <Form.Group>
                        <Form.Label>Idioma</Form.Label>
                        <Form.Select value={collectionForm.catalogLanguage} onChange={event => {
                          setCatalogSetSearch("");
                          setCollectionForm(current => ({
                            ...current,
                            catalogLanguage: event.target.value,
                            cardSetId: ""
                          }));
                        }} required>
                          {catalogLanguages.map(value => <option value={value} key={value}>{catalogLanguageLabel(value)}</option>)}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="binder-set-search">
                        <Form.Label>Buscar set</Form.Label>
                        <div className="binder-set-search-control">
                          <i className="bi bi-search" />
                          <Form.Control value={catalogSetSearch} onChange={event => setCatalogSetSearch(event.target.value)} placeholder="Nombre, codigo o alias..." autoComplete="off" />
                          {catalogSetSearch && <button type="button" onClick={() => setCatalogSetSearch("")} aria-label="Limpiar busqueda"><i className="bi bi-x-lg" /></button>}
                        </div>
                      </Form.Group>
                    </div>
                    <div className="binder-set-picker-meta">
                      <span>{filteredCatalogSets.length} sets en {catalogLanguageLabel(collectionForm.catalogLanguage)}</span>
                      {collectionForm.cardSetId && <strong><i className="bi bi-check-circle-fill" /> Set seleccionado</strong>}
                    </div>
                    {filteredCatalogSets.length ? (
                      <div className="binder-set-grid" role="listbox" aria-label="Sets disponibles">
                        {filteredCatalogSets.map(set => {
                          const selected = collectionForm.cardSetId === set.id;
                          return (
                            <button type="button" role="option" aria-selected={selected} className={"binder-set-option " + (selected ? "is-selected" : "")} key={set.id} onClick={() => setCollectionForm(current => ({
                              ...current,
                              cardSetId: set.id,
                              name: set.name || "",
                              targetTotal: baseSetTarget(set) || current.targetTotal
                            }))}>
                              <span className="binder-set-option-logo">
                                <CatalogSetImage set={set} />
                              </span>
                              <span className="binder-set-option-copy">
                                <strong>{set.name}</strong>
                                <small>{[set.code, set.total_cards ? set.total_cards + " cartas" : ""].filter(Boolean).join(" / ")}</small>
                              </span>
                              <i className={"bi " + (selected ? "bi-check-circle-fill" : "bi-circle")} />
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="binder-set-empty">
                        <i className="bi bi-search" />
                        <strong>No encontramos ese set</strong>
                        <span>Prueba por nombre, codigo o cambia el idioma.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {collectionStep === 3 && (
              <div className="binder-wizard-panel">
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label>Visibilidad</Form.Label>
                    <Form.Select value={collectionForm.visibility} onChange={event => setCollectionForm({ ...collectionForm, visibility: event.target.value })}>
                      <option value="public">Publico</option>
                      <option value="followers">Seguidores</option>
                      <option value="private">Privado</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>
                      Objetivo de cartas{collectionForm.binderStructure === "set" ? " (set base)" : ""} <span className="text-muted">(opcional)</span>
                    </Form.Label>
                    <Form.Control type="number" min="1" value={collectionForm.targetTotal} onChange={event => setCollectionForm({ ...collectionForm, targetTotal: event.target.value })} />
                    {collectionForm.binderStructure === "set" && <Form.Text>Usa el total oficial, sin separar variantes de acabado.</Form.Text>}
                  </Col>
                  <Col xs={12}>
                    <Form.Label>Descripcion</Form.Label>
                    <Form.Control as="textarea" rows={3} value={collectionForm.description} onChange={event => setCollectionForm({ ...collectionForm, description: event.target.value })} placeholder="Objetivo, variantes o detalles del binder..." />
                  </Col>
                </Row>
                <div className="binder-wizard-summary">
                  <span><i className={collectionForm.binderStructure === "set" ? "bi bi-layers" : collectionForm.themeType ? "bi bi-stars" : "bi bi-grid-3x3-gap"} /> {collectionForm.binderStructure === "set" ? "Por set" : collectionForm.themeType ? "Por temática" : "Personalizado"}</span>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            {collectionStep > 1 && <Button variant="outline-dark" disabled={isSavingCollection} onClick={() => setCollectionStep(step => step - 1)}>Atras</Button>}
            <Button type="submit" className="btn-cardora" disabled={isSavingCollection || (collectionStep === 1 ? !collectionForm.binderStructure : collectionStep === 2 && (collectionForm.binderStructure === "set" ? !collectionForm.cardSetId : !collectionForm.name.trim() || (collectionForm.themeType && !collectionForm.themeQuery.trim())))}>
              {isSavingCollection
                ? <><Spinner size="sm" className="me-2" />{editingCollectionId ? "Guardando..." : "Creando..."}</>
                : collectionStep < 3 ? "Continuar" : editingCollectionId ? "Guardar binder" : "Crear binder"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

            <Modal show={showEditModal} onHide={closeEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar carta</Modal.Title>
        </Modal.Header>
        <Form onSubmit={saveBinderCard}>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}
            <Row className="g-3">
              <Col md={12}>
                <Form.Label>Visibilidad</Form.Label>
                <Form.Select
                  value={editForm.visibility}
                  onChange={event => setEditForm({ ...editForm, visibility: event.target.value })}
                >
                  <option value="public">Publica</option>
                  <option value="followers">Seguidores</option>
                  <option value="private">Privada</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Condicion</Form.Label>
                <Form.Select
                  value={editForm.condition}
                  onChange={event => setEditForm({ ...editForm, condition: event.target.value })}
                >
                  <option>Near Mint</option>
                  <option>Excellent</option>
                  <option>Good</option>
                  <option>Played</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Acabado de esta copia</Form.Label>
                <Form.Select value={editForm.cardFinish} onChange={event => setEditForm({ ...editForm, cardFinish: event.target.value })}>
                  {CARD_FINISH_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Copias</Form.Label>
                <div className="binder-quantity-stepper compact"><button type="button" onClick={() => setEditForm(current => { const quantity = Math.max(1, current.quantity - 1); return { ...current, quantity, availableQuantity: Math.min(current.availableQuantity, quantity) }; })}><i className="bi bi-dash" /></button><strong>{editForm.quantity}</strong><button type="button" onClick={() => setEditForm(current => ({ ...current, quantity: Math.min(99, current.quantity + 1) }))}><i className="bi bi-plus" /></button></div>
              </Col>
              <Col md={3}>
                <Form.Label>Para intercambio</Form.Label>
                <Form.Check type="switch" id="edit-trade-toggle" checked={editForm.availableQuantity > 0} onChange={event => setEditForm(current => ({ ...current, availableQuantity: event.target.checked ? Math.max(1, current.availableQuantity) : 0, cardIntention: event.target.checked ? "for_trade" : "collection", status: event.target.checked ? "for_trade" : "showcase" }))} label={editForm.availableQuantity > 0 ? `${editForm.availableQuantity} para intercambio` : "Solo en mi coleccion"} />
                {editForm.availableQuantity > 0 && <div className="binder-quantity-stepper compact mt-2"><button type="button" onClick={() => setEditForm(current => ({ ...current, availableQuantity: Math.max(1, current.availableQuantity - 1) }))}><i className="bi bi-dash" /></button><strong>{editForm.availableQuantity}</strong><button type="button" onClick={() => setEditForm(current => ({ ...current, availableQuantity: Math.min(current.quantity, current.availableQuantity + 1) }))}><i className="bi bi-plus" /></button></div>}
              </Col>
              <Col md={6}>
                <Form.Label>Coleccion</Form.Label>
                <Form.Select
                  value={editForm.collectionId}
                  onChange={event => setEditForm({ ...editForm, collectionId: event.target.value })}
                >
                  <option value="">Sin coleccion</option>
                  {collections.map(collection => (
                    <option value={collection.id} key={collection.id}>
                      {collection.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Foto frontal</Form.Label>
                <div className="edit-card-photo-preview">
                  {(selectedPhotoPreview || editForm.frontImage)
                    ? <img src={selectedPhotoPreview || editForm.frontImage} alt="Foto frontal actual" />
                    : <span>Sin foto frontal</span>}
                </div>
                <Form.Control
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={event => processPhoto(event.target.files?.[0], photo => setEditedPhoto("front", photo), "front")}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline-dark"
                  className="mt-2"
                  disabled={isProcessingPhoto || (!editForm.frontImage && !selectedPhotoOriginal)}
                  onClick={() => selectedPhotoOriginal ? setAdjustingPhotoSide("front") : adjustExistingCardPhoto("front")}
                >
                  <i className="bi bi-crop me-2" />Ajustar encuadre
                </Button>
              </Col>
              <Col md={6}>
                <Form.Label>Foto reverso</Form.Label>
                <div className="edit-card-photo-preview">
                  {(selectedBackPhotoPreview || editForm.backImage)
                    ? <img src={selectedBackPhotoPreview || editForm.backImage} alt="Foto reverso actual" />
                    : <span>Sin foto de reverso</span>}
                </div>
                <Form.Control
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={event => processPhoto(event.target.files?.[0], photo => setEditedPhoto("back", photo), "back")}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline-dark"
                  className="mt-2"
                  disabled={isProcessingPhoto || (!editForm.backImage && !selectedBackPhotoOriginal)}
                  onClick={() => selectedBackPhotoOriginal ? setAdjustingPhotoSide("back") : adjustExistingCardPhoto("back")}
                >
                  <i className="bi bi-crop me-2" />Ajustar encuadre
                </Button>
              </Col>
              <Col xs={12}>
                <Form.Label>Notas de trade</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editForm.tradeNotes}
                  onChange={event => setEditForm({ ...editForm, tradeNotes: event.target.value })}
                  placeholder="Busco cartas de Pokemon 151, preferencia entrega en mano..."
                />
              </Col>
              <Col md={4}>
                <Form.Label>Valor estimado EUR</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={editForm.estimatedValue}
                  onChange={event => setEditForm({ ...editForm, estimatedValue: event.target.value })}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-dark" onClick={closeEditModal}>Cancelar</Button>
            <Button type="submit" className="btn-cardora" disabled={isSavingCard || isProcessingPhoto}>
              {isSavingCard ? <><Spinner size="sm" className="me-2" />Guardando...</> : "Guardar"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default BinderHome;
